import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { doc, onSnapshot, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Radio,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Chip,
  IconButton,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';
import {
  Save as SaveIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  FlashOn as ImmediateIcon,
  Bookmarks as TemplateIcon,
  Bookmark as BookmarkIcon,
  Repeat as RecurringIcon,
  Timer as TimerIcon,
  DirectionsCar as CarIcon,
  AirportShuttle as KombiIcon,
  ElectricRickshaw as MinibusIcon,
  SwapVert as SwapIcon,
  LocalHospital as MedicalIcon,
  Accessible as WheelchairIcon,
  EventSeat as TransportChairIcon,
  Hotel as StretcherIcon,
  Euro as EuroIcon,
  NearMe as AnfahrtIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Close as CloseIcon,
  AddLocationAlt as AddLocationAltIcon
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { createGlobalStyle } from 'styled-components';
import { APIProvider } from '@vis.gl/react-google-maps';
import PlaceAutocomplete from '../common/PlaceAutocomplete';
import DispatchInputHelpDialog from './DispatchInputHelpDialog';
import useFormKeyboardNav from '../../hooks/useFormKeyboardNav';
import { NO_AUTOFILL, createFieldIdFactory } from '../../utils/noAutofill';

import { 
  fetchRideById, 
  createRide,
  assignRide,
  updateRide,
  fetchRideTemplates,
  saveRideTemplate
} from '../../redux/slices/ridesSlice';
import { fetchDrivers } from '../../redux/slices/driversSlice';
import { fetchVehicles } from '../../redux/slices/vehiclesSlice';
import { fetchCustomers, createCustomer } from '../../redux/slices/customersSlice';
import { createMedicalTransportOrder, fetchMedicalTransportOrders } from '../../redux/slices/medicalTransportOrdersSlice';
import { fetchSettings } from '../../redux/slices/settingsSlice';
import { calculateEstimatedFareEur, isNachtTarif, spezialZuschlag, spezialZuschlagLabel } from '../../utils/fareEstimate';
import { filterSelectableDrivers } from '../../utils/driverActive';
import { geocodeAddressToLocation, hasCoordinates } from '../../utils/addressGeocoding';
import { getServiceAreaBias } from '../../utils/serviceAreaBias';
import {
  shouldChargeAnfahrt,
  nearestBoundaryPoint,
  haversineMeters,
  calcAnfahrtFromMeters,
  effectiveAnfahrtPreisProKm,
  buildAnfahrtNote,
  stripAnfahrtNote,
} from '../../utils/anfahrt';
import { VEHICLE_TYPE_OPTIONS, SPECIAL_CAPABILITIES, getVehicleTypeDescription, isVehicleCompatible, migrateSpecialVehicleType } from '../../config/vehicleTypes';
import { APPROVAL_STATUS_OPTIONS, formatDate, getOption } from '../medicalTransportOrders/medicalTransportOrderOptions';
import { buildMedicalTransportSnapshot } from '../medicalTransportOrders/medicalTransportSnapshot';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import { useTransportWording } from '../../utils/transportWording';
import { MAX_INTERMEDIATE_STOPS, buildStopFields, getRideStops } from '../../utils/rideStops';
import { activeVehiclesOnly } from '../../utils/vehicleActive';

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const DEFAULT_PHONE_CUSTOMER_NAME = 'Laufkunde';

const toDateOrNull = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (value) => {
  const date = toDateOrNull(value);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfDay = (value) => {
  const date = toDateOrNull(value);
  if (!date) return null;
  date.setHours(23, 59, 59, 999);
  return date;
};

const countRecurringDates = (startValue, endValue, weekdays = []) => {
  const start = startOfDay(startValue);
  const end = endOfDay(endValue);
  if (!start || !end || !Array.isArray(weekdays) || weekdays.length === 0 || end < start) return 0;

  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    if (weekdays.includes(cursor.getDay())) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

// Synthetische Autocomplete-Option für die Verordnungs-Schnellanlage
// (KONZEPT_KRANKENFAHRT_VERORDNUNGSPFLICHT §8.1): legt beim Speichern der
// Fahrt eine minimale Platzhalter-Verordnung an, Patient kommt automatisch
// aus dem Fahrt-Kunden.
const MEDICAL_ORDER_CREATE_OPTION = { isCreatePlaceholder: true };



// Globale Styles für Google Maps Autocomplete
const GlobalStyle = createGlobalStyle`
  .pac-container {
    z-index: 1500 !important;
    border-radius: 4px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    position: fixed !important; /* Wichtig: fixed positioning für bessere Scroll-Behandlung */
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: white;
    
    
    /* Mobile Anpassungen */
    @media (max-width: 768px) {
      max-height: 150px;
      font-size: 14px;
    }
    
    .pac-item {
      padding: 12px 16px;
      cursor: pointer;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      transition: background-color 0.2s ease;
      line-height: 1.4;
      
      
      &:last-child {
        border-bottom: none;
      }
      
      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }
      
      &.pac-item-selected {
        background-color: rgba(25, 118, 210, 0.08);
      }
      
      /* Mobile Anpassungen */
      @media (max-width: 768px) {
        padding: 10px 12px;
        min-height: 44px; /* Mindesthöhe für Touch-Geräte */
      }
    }
    
    .pac-icon {
      display: none !important;
    }
    
    .pac-item-query {
      font-size: 14px;
      color: rgba(0, 0, 0, 0.87);
      
      
      @media (max-width: 768px) {
        font-size: 13px;
      }
    }
    
    .pac-matched {
      font-weight: 600;
      color: #1976d2;
    }
    
    .pac-item-context {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);
      margin-top: 2px;
      
      @media (max-width: 768px) {
        font-size: 11px;
      }
    }
  }
  
  /* Verhindern, dass das Dropdown bei Touch-Geräten zu schnell verschwindet */
  @media (max-width: 768px) {
    .pac-container {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
  }
`;



const OrderTypeBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
}));

// Fahrzeugnummer aus Fahrer-/Fahrzeugobjekt – analog LiveMap.js (vehicleByDriverId
// nicht verfügbar, also direkt vom Fahrer/car-Property).
const getDriverVehicleNumber = (driver) => {
  if (!driver) return '';
  return (driver.vehicle?.vehicleNumber || driver.car?.vehicleNumber || driver.vehicleNumber || '')
    .toString()
    .trim();
};

const getDriverVehicleInfo = (driver) => {
  const vehicle = driver?.vehicle || driver?.car || null;
  if (!vehicle) return null;
  return {
    number: (vehicle.vehicleNumber || '').toString().trim(),
    model: vehicle.model || 'Unbekannt',
    licensePlate: vehicle.licensePlate || 'Kein Kennzeichen',
  };
};

// Status-Label analog LiveMap. Ohne aktive Fahrt-Info nur grobe Unterscheidung
// (Verfügbar / Besetzt). Aufrufer entscheidet, ob das Label gezeigt wird.
const getDriverStatusLabel = (driver) => {
  if (!driver?.isOnline) return 'Offline';
  if (driver.isAvailable) return 'Verfügbar';
  return 'Besetzt';
};

const getDriverContact = (driver) => {
  if (driver?.driverNumber) return `Fahrer-Nr: ${driver.driverNumber}`;
  const phone = driver?.phone || driver?.user?.phone;
  return phone ? `Tel: ${phone}` : 'Tel: N/A';
};

const getDriverOptionLabel = (option) => {
  const name = option.name || option.user?.name || `Fahrer ${option.id}`;
  const vehicleNumber = getDriverVehicleNumber(option);
  return vehicleNumber ? `${vehicleNumber} · ${name}` : name;
};

// renderOption für die Fahrer-Autocomplete im RideForm.
// `requireOnlineWithVehicle = true` → Sofortbestellung: Fahrzeugnummer/Status/
// Fahrzeug werden immer gezeigt (Filter sichert ohnehin Online + Fahrzeug).
// `false` → Vor-/Dauerbestellung: Block nur, wenn Fahrer online + Fahrzeug.
// `mismatchReason` → Fahrzeug passt nicht zur Anforderung der Fahrt; `blocking`
// sagt, ob das die Auswahl sperrt (Sofortbestellung) oder nur ein Hinweis ist.
const renderDriverOption = (props, option, { requireOnlineWithVehicle, mismatchReason = '', blocking = false }) => {
  const name = option.name || option.user?.name || `Fahrer ${option.id}`;
  const vehicleInfo = getDriverVehicleInfo(option);
  const showVehicleDetails = requireOnlineWithVehicle || (option.isOnline && vehicleInfo);
  const statusLabel = showVehicleDetails ? getDriverStatusLabel(option) : 'Offline';
  const ratingLabel = option.rating?.toFixed ? option.rating.toFixed(1) : (option.rating || 'N/A');
  const contactLabel = getDriverContact(option);

  return (
    <li {...props}>
      <Box>
        <Typography variant="body1">
          {showVehicleDetails && vehicleInfo?.number ? `${vehicleInfo.number} · ` : ''}{name}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {statusLabel} | Rating: {ratingLabel} | {contactLabel}
        </Typography>
        {showVehicleDetails && vehicleInfo && (
          <Typography variant="body2" color="textSecondary">
            Fahrzeug: {vehicleInfo.number ? `${vehicleInfo.number} · ` : ''}{vehicleInfo.model} ({vehicleInfo.licensePlate})
          </Typography>
        )}
        {mismatchReason && (
          <Typography variant="body2" color={blocking ? 'error.main' : 'warning.main'}>
            {blocking ? 'Passt nicht: ' : 'Hinweis: '}{mismatchReason}
          </Typography>
        )}
      </Box>
    </li>
  );
};

// Wartezeit-Severity analog zum Dashboard (grün/orange/rot je nach Wartezeit)
const getWaitTimeSeverity = (waitTime) => {
  const value = String(waitTime || '');
  if (value.includes('nicht verfügbar') ||
      value.includes('Keine Fahrer verfügbar') ||
      value.includes('30+') ||
      value.includes('Fehler')) return 'error';
  if (value.includes('nach Ausfallzeit') ||
      value.startsWith('2') ||
      value.startsWith('3')) return 'warning';
  return 'success';
};

const RideForm = ({ mode, rideId, initialDraft, helpRef, onClose, onSuccess } = {}) => {
  const wording = useTransportWording();

  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Gemessene Hoehe der mobil fixierten Buttonleiste (siehe Platzhalter unten).
  const actionBarRef = useRef(null);
  const [actionBarHeight, setActionBarHeight] = useState(0);

  // RideForm läuft ausschließlich als Dialog (siehe RideFormDialog);
  // Modus/Id/Entwurf kommen aus Props, nicht aus der Route.
  const draftState = useMemo(() => initialDraft || {}, [initialDraft]);
  const isAssignMode = mode === 'assign';
  const isEditMode = mode === 'edit';
  
  const { currentRide, templates, isLoading: rideLoading, error: rideError } = useSelector(state => state.rides);
  const currentUser = useSelector(state => state.auth.user);
  const { global: globalSettings } = useSelector(state => state.settings);
  const { drivers, isLoading: driversLoading } = useSelector(state => state.drivers);
  const { vehicles, isLoading: vehiclesLoading } = useSelector(state => state.vehicles);
  const { customers, isLoading: customersLoading } = useSelector(state => state.customers);
  const {
    orders: medicalTransportOrders,
    isLoading: medicalTransportOrdersLoading
  } = useSelector(state => state.medicalTransportOrders);

  // Echtzeit-Wartezeiten aus Firebase
  const [waitTimes, setWaitTimes] = useState(null);
  const waitTimeUnsubRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'waitTimes', 'current'), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setWaitTimes({
          general: d.general || '–',
          grossraum: d.grossraum || '–',
          kleinbus7: d.kleinbus7 || '–',
          kleinbus8: d.kleinbus8 || '–',
        });
      }
    });
    waitTimeUnsubRef.current = unsub;
    return () => unsub();
  }, []);

  // Aktive Rechnungskunden laden (für die Rechnungsfahrt-Zuordnung).
  useEffect(() => {
    let active = true;
    getDocs(query(collection(db, 'invoiceCustomers'), orderBy('displayName')))
      .then((snap) => {
        if (!active) return;
        setInvoiceCustomers(
          snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.active !== false)
        );
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Ermittle welche Fahrzeugtypen tatsächlich existieren
  const existingVehicleTypes = useMemo(() => {
    // Reservefahrzeuge (deaktiviert) sind nicht einsetzbar und zählen nicht.
    const fleet = activeVehiclesOnly(vehicles);
    if (fleet.length === 0) return new Set([1]);
    const types = new Set();
    fleet.forEach(v => {
      const vType = Number(v.vehicleType || v.vehicle_type || 1);
      types.add(vType);
    });
    return types;
  }, [vehicles]);

  // Spezialfähigkeiten im tatsächlichen Fahrzeugbestand.
  //
  // Pro Fähigkeit wird festgehalten, ob sie im Bestand überhaupt vorkommt
  // (`hasAny`) und ob eines dieser Fahrzeuge gerade einem online angemeldeten
  // Fahrer zugewiesen ist (`isAvailable`). Pause oder laufende Fahrt zählen
  // weiterhin als verfügbar — der Fahrer ist im Dienst. Es ist dieselbe
  // Definition von „im Dienst", nach der weiter unten auch die Fahrerauswahl
  // für Sofortbestellungen gefiltert wird.
  //
  // Der Fahrzeugtyp geht hier bewusst NICHT ein: Ein fünfsitziger Caddy wird je
  // nach Betrieb als PKW oder als Großraum angelegt und kann trotzdem ein
  // Rollstuhlfahrzeug sein. Das Fahrzeugformular hat die Typ-Kopplung deshalb
  // bereits aufgegeben (0e18bd2); die Bestellmaske zieht hier nach.
  const capabilityFleetStatus = useMemo(() => {
    const driverById = (drivers || []).reduce((map, d) => {
      if (d?.id) map[d.id] = d;
      return map;
    }, {});

    const status = {};
    for (const vehicle of activeVehiclesOnly(vehicles)) {
      const caps = Array.isArray(vehicle.specialCapabilities) ? vehicle.specialCapabilities : [];
      if (caps.length === 0) continue;
      const driverId = vehicle.driverId || vehicle.current_driver?.id || vehicle.current_driver_id || null;
      const driver = driverId ? driverById[driverId] : null;
      const onDuty = !!(driver && driver.isOnline);
      for (const cap of caps) {
        const entry = status[cap] || { hasAny: false, isAvailable: false };
        entry.hasAny = true;
        if (onDuty) entry.isAvailable = true;
        status[cap] = entry;
      }
    }
    return status;
  }, [drivers, vehicles]);

  // Verfügbarkeits-Chips im Kopf der Maske: nur die Krankenfahrt-Fahrzeuge,
  // und nur solche, die es im Bestand überhaupt gibt.
  const specialCapabilityItems = useMemo(() => {
    const definitions = [
      { key: 'rollstuhl', label: 'Rollstuhl', icon: <WheelchairIcon /> },
      { key: 'tragestuhl', label: 'Tragestuhl', icon: <TransportChairIcon /> },
      { key: 'liegend', label: 'Liegend', icon: <StretcherIcon /> },
    ];
    return definitions
      .map((def) => ({ ...def, ...(capabilityFleetStatus[def.key] || { hasAny: false, isAvailable: false }) }))
      .filter((item) => item.hasAny);
  }, [capabilityFleetStatus]);

  const [formData, setFormData] = useState({
    // Ride Information
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    pickup_address: '',
    pickup_location: { latitude: null, longitude: null },
    destination_address: '',
    destination_location: { latitude: null, longitude: null },
    // Zwischenstopps OHNE Endziel, in anzufahrender Reihenfolge
    // (zwischenstopps_konzept.md §3.2). Jeder Eintrag:
    // { address, location: { latitude, longitude } }
    stops: [],
    order_type: '2', // 2: Sofortbestellung, 1: Vorbestellung, 3: Dauerauftrag
    scheduled_time: null,
    vehicle_type: '1', // 1: PKW, 2: Großraum, 3: Kleinbus 7, 4: Kleinbus 8
    special_capabilities: [], // Spezialfähigkeiten als Array
    notes: '', // Notizen für den Fahrer
    is_krankenfahrt: false, // Krankenfahrt
    medical_transport_order_id: '',
    is_rechnungsfahrt: false, // Rechnungsfahrt
    invoice_customer_id: '', // Rechnungskunde (Firma) bei Rechnungsfahrt
    is_festpreis: false,
    festpreis_eur: '',

    // Recurring Order Information (for order_type '3')
    recurring_start_date: null,
    recurring_end_date: null,
    recurring_time: null,
    recurring_weekdays: [], // Array of weekday numbers (0=Sunday, 1=Monday, etc.)
    
    // Assignment Information (for assign mode or direct assignment)
    driver_id: '',
    vehicle_id: ''
  });
  
  const [createNewCustomer, setCreateNewCustomer] = useState(false);
  // Verordnungs-Schnellanlage gewählt: beim Speichern wird eine unvollständige
  // Platzhalter-Verordnung angelegt und sofort mit der Fahrt verknüpft.
  const [createNewMedicalOrder, setCreateNewMedicalOrder] = useState(false);
  // Bewusste Übersteuerung eines harten Verordnungs-Hinweises (Kontingent
  // überschritten / Fahrt außerhalb des Verordnungszeitraums): Der Disponent
  // kann trotz Warnung anlegen, muss die Übersteuerung aber aktiv bestätigen.
  const [medicalOverrideAck, setMedicalOverrideAck] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  // Rechnungskunden (Firmen) für die Rechnungsfahrt-Zuordnung.
  const [invoiceCustomers, setInvoiceCustomers] = useState([]);
  // Rechnungskunden-Schnellanlage (§8.4): getippter Firmenname; beim Speichern
  // legt die CF saveInvoiceCustomer (quickCreate) einen unvollständigen
  // Datensatz an — Anschrift/Rechnungs-E-Mail pflegt die Zentrale nach.
  const [newInvoiceCustomerName, setNewInvoiceCustomerName] = useState('');
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  // Adresstexte, zu denen die aktuell hinterlegten Koordinaten gehören. Weicht
  // der Feldinhalt davon ab, wurde von Hand getippt und die Koordinaten des
  // zuvor gewählten Vorschlags sind hinfällig.
  const selectedAddressRef = useRef({ pickup: null, destination: null });

  // Live-Preisschätzung (Tarif + ggf. Anfahrtskosten) für die Anzeige im
  // Formular. `key` bindet das Ergebnis an die aktuellen Eingaben, damit der
  // Submit-Pfad denselben Wert ohne erneute Directions-/Anfahrts-Calls nutzt.
  const [fareEstimate, setFareEstimate] = useState(null);
  const [isCalculatingFare, setIsCalculatingFare] = useState(false);
  const fareEstimateRef = useRef(null);
  
  const [formErrors, setFormErrors] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    pickup_address: '',
    destination_address: '',
    scheduled_time: '',
    recurring_start_date: '',
    recurring_end_date: '',
    recurring_time: '',
    recurring_weekdays: '',
    medical_transport_order_id: '',
    driver_id: '',
    vehicle_id: ''
  });
  
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  
  useEffect(() => {
    if ((isEditMode || isAssignMode) && rideId) {
      dispatch(fetchRideById(rideId));
    }

    dispatch(fetchDrivers());
    dispatch(fetchVehicles());
    dispatch(fetchCustomers());
    dispatch(fetchRideTemplates());
    dispatch(fetchMedicalTransportOrders());
    dispatch(fetchSettings()); // Tarif + Anfahrtskosten für die Preisschätzung
  }, [dispatch, rideId, isEditMode, isAssignMode]);
  
  
  useEffect(() => {
    if (currentRide && (isEditMode || isAssignMode)) {
      setFormData({
        ...formData,
        customer_id: currentRide.customerId || '',
        customer_name: currentRide.customerName || '',
        pickup_address: currentRide.start || '',
        pickup_location: {
          latitude: currentRide.startLatitude || null,
          longitude: currentRide.startLongitude || null
        },
        destination_address: currentRide.destination || '',
        destination_location: {
          latitude: currentRide.destinationLatitude || null,
          longitude: currentRide.destinationLongitude || null
        },
        // Der letzte Halt IST das Endziel (§2.2) und steckt bereits im
        // Zieladressfeld — hier nur die Zwischenstopps davor.
        stops: getRideStops(currentRide)
          .slice(0, -1)
          .map((stop) => ({
            address: stop.address,
            location: { latitude: stop.latitude, longitude: stop.longitude },
          })),
        order_type: currentRide.orderType?.toString() || '2',
        scheduled_time: currentRide.scheduledTime ? new Date(currentRide.scheduledTime) : null,
        vehicle_type: currentRide.vehicleType?.toString() || '1',
        special_capabilities: currentRide.specialCapabilities || migrateSpecialVehicleType(currentRide),
        notes: currentRide.notes || '',
        is_krankenfahrt: currentRide.isKrankenfahrt || false,
        medical_transport_order_id: currentRide.medicalTransportOrderId || '',
        is_rechnungsfahrt: currentRide.isRechnungsfahrt || false,
        invoice_customer_id: currentRide.invoiceCustomerId || '',
        is_festpreis: currentRide.isFestpreis || false,
        festpreis_eur: currentRide.festpreisEur || '',
        driver_id: currentRide.driverId || '',
        vehicle_id: currentRide.vehicle?.id || ''
      });
    }
    // formData bewusst nicht in den Deps: der Effekt befüllt formData nur einmal
    // beim Laden der Fahrt; ein Dep würde eine Endlosschleife auslösen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRide, isEditMode, isAssignMode]);

  // Fahrt erneut erstellen: Daten aus vorheriger Fahrt vorausfüllen
  useEffect(() => {
    const duplicateRide = draftState.duplicateRide;
    if (duplicateRide && !isEditMode && !isAssignMode) {
      const isActiveSource = !['completed', 'cancelled', 'cancelled_by_driver'].includes(duplicateRide.status);
      // Bei laufender Quelle (2. Taxi): immer als Sofortbestellung anlegen
      const orderType = isActiveSource ? '2' : (duplicateRide.orderType?.toString() || '2');

      // Für Vorbestellungen: scheduled_time übernehmen wenn in der Zukunft, sonst +1 Stunde
      let scheduledTime = null;
      if (orderType === '1') {
        const originalTime = duplicateRide.scheduledTime
          ? new Date(duplicateRide.scheduledTime)
          : null;
        if (originalTime && originalTime > new Date()) {
          scheduledTime = originalTime;
        } else {
          scheduledTime = new Date(Date.now() + 60 * 60 * 1000);
        }
      }

      // Für Daueraufträge: Standardwerte setzen, Wochentage und Zeit übernehmen
      let recurringStartDate = null;
      let recurringEndDate = null;
      let recurringTime = null;
      let recurringWeekdays = [];
      if (orderType === '3') {
        recurringStartDate = new Date(Date.now() + 24 * 60 * 60 * 1000);
        recurringEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
        recurringTime = duplicateRide.recurringTime
          ? new Date(duplicateRide.recurringTime)
          : new Date(new Date().setHours(8, 0, 0, 0));
        recurringWeekdays = duplicateRide.recurringWeekdays || [];
      }

      setFormData(prev => ({
        ...prev,
        customer_id: duplicateRide.customerId || '',
        customer_name: duplicateRide.customerName || '',
        customer_phone: duplicateRide.customerPhone || '',
        pickup_address: duplicateRide.start || '',
        pickup_location: {
          latitude: duplicateRide.startLatitude || null,
          longitude: duplicateRide.startLongitude || null
        },
        destination_address: duplicateRide.destination || '',
        destination_location: {
          latitude: duplicateRide.destinationLatitude || null,
          longitude: duplicateRide.destinationLongitude || null
        },
        order_type: orderType,
        vehicle_type: duplicateRide.vehicleType?.toString() || '1',
        special_capabilities: duplicateRide.specialCapabilities || migrateSpecialVehicleType(duplicateRide),
        notes: duplicateRide.notes || '',
        is_krankenfahrt: duplicateRide.isKrankenfahrt || false,
        medical_transport_order_id: duplicateRide.medicalTransportOrderId || '',
        is_rechnungsfahrt: duplicateRide.isRechnungsfahrt || false,
        invoice_customer_id: duplicateRide.invoiceCustomerId || '',
        is_festpreis: duplicateRide.isFestpreis || false,
        festpreis_eur: duplicateRide.festpreisEur || '',
        scheduled_time: scheduledTime,
        recurring_start_date: recurringStartDate,
        recurring_end_date: recurringEndDate,
        recurring_time: recurringTime,
        recurring_weekdays: recurringWeekdays,
        // Fahrer und Fahrzeug werden NICHT übernommen
        driver_id: '',
        vehicle_id: ''
      }));
    }
  }, [draftState.duplicateRide, isEditMode, isAssignMode]);

  // Eingehender Telefonanruf: Kundendaten als noch ungespeicherten Fahrtentwurf uebernehmen.
  useEffect(() => {
    const incomingCallDraft = draftState.incomingCallDraft;
    if (!incomingCallDraft || isEditMode || isAssignMode) return;

    setFormData(prev => ({
      ...prev,
      customer_id: incomingCallDraft.customerId || '',
      customer_name: incomingCallDraft.customerName || '',
      customer_phone: incomingCallDraft.customerPhone || '',
      pickup_address: incomingCallDraft.pickupAddress || '',
      order_type: '2'
    }));
    setCreateNewCustomer(false);
  }, [draftState.incomingCallDraft, isEditMode, isAssignMode]);

  // Übernahme bereits eingegebener Daten (z. B. aus der kompakten QuickRideForm),
  // um sie im großen Formular weiter auszufüllen. `prefill` enthält formData-Felder.
  useEffect(() => {
    const prefill = draftState.prefill;
    if (!prefill || isEditMode || isAssignMode) return;

    const keys = [
      'customer_name', 'customer_phone', 'customer_id',
      'pickup_address', 'pickup_location',
      'destination_address', 'destination_location',
      'vehicle_type', 'order_type', 'notes', 'driver_id', 'scheduled_time'
    ];
    setFormData(prev => {
      const next = { ...prev };
      keys.forEach(k => { if (prefill[k] !== undefined) next[k] = prefill[k]; });
      return next;
    });
    // Kundenanzeige: echter Kunde → Auswahl; nur Name/Telefon → als Telefonkunde; sonst Laufkunde.
    if (prefill.customer_id) {
      setCreateNewCustomer(false);
    } else if (prefill.customer_name && prefill.customer_name.trim()) {
      setCreateNewCustomer(true);
    } else {
      setCreateNewCustomer(false);
    }
  }, [draftState.prefill, isEditMode, isAssignMode]);

  useEffect(() => {
    // Filter available drivers (only those with 'available' status)
    if (drivers && drivers.length > 0) {
      // Deaktivierte Fahrer gehören in keine Auswahlliste — der bereits
      // zugewiesene Fahrer bleibt aber sichtbar, sonst fiele er beim
      // Bearbeiten einer älteren Fahrt stillschweigend aus dem Feld.
      const selectableDrivers = filterSelectableDrivers(drivers, currentRide?.driverId || null);

      // Für Sofortbestellungen: nur Online-Fahrer MIT zugewiesenem Fahrzeug
      const onlineDrivers = selectableDrivers.filter(driver => {
        const isCurrentDriver = isAssignMode && currentRide && driver.id === currentRide.driverId;
        const hasVehicle = !!(driver.vehicle || driver.car);
        return (driver.isOnline && hasVehicle) || isCurrentDriver;
      });
      setAvailableDrivers(onlineDrivers);

      // Speichere alle Fahrer für Vorbestellungen (keine Fahrzeug-Einschränkung)
      setAllDrivers(selectableDrivers);
    }
    
    // Fahrzeuge: Im Zuweisungsmodus alle Fahrzeuge anzeigen, sonst nur aktive/verfügbare
    if (vehicles && vehicles.length > 0) {
      if (isAssignMode) {
        setAvailableVehicles(vehicles);
      } else {
        const filteredVehicles = vehicles.filter(vehicle =>
          (vehicle.status === 'active' || vehicle.status === 'available') ||
          (currentRide && vehicle.id === currentRide.vehicle?.id)
        );
        setAvailableVehicles(filteredVehicles);
      }
    }
  }, [drivers, vehicles, isAssignMode, currentRide]);

  const selectedMedicalTransportOrder = useMemo(
    () => medicalTransportOrders.find(order => order.id === formData.medical_transport_order_id) || null,
    [medicalTransportOrders, formData.medical_transport_order_id]
  );

  // Auswahlliste "Spezialfähigkeiten" — aus dem Fahrzeugbestand abgeleitet.
  //
  // Sofortbestellung: Anfordern lässt sich nur, was gerade im Dienst ist. Eine
  // Fähigkeit, die kein eingeloggter Fahrer bietet, ist im Dispatch ein harter
  // Filter (functions/src/index.ts, `specialCapabilities`) — die Fahrt bekäme
  // niemand angeboten. Nicht Verfügbares wird deshalb ausgegraut mit Grund
  // gezeigt und nicht weggelassen: "steht nicht in der Liste" war genau die
  // Rückmeldung, die uns erreicht hat.
  //
  // Vorbestellung und Dauerauftrag: alles, was im Bestand vorkommt. Wer morgen
  // früh fährt, muss heute Abend nicht angemeldet sein.
  //
  // Bereits gewählte Fähigkeiten bleiben immer anklickbar — beim Bearbeiten,
  // aus einer Vorlage oder einem Duplikat. Deaktivierte Einträge nehmen keinen
  // Klick an; ausgegraut ließen sie sich nicht mehr abwählen und fielen beim
  // Speichern stillschweigend heraus.
  const specialCapabilityOptions = useMemo(() => {
    const selected = formData.special_capabilities || [];
    const isSofortbestellung = formData.order_type === '2';

    const keys = Object.keys(SPECIAL_CAPABILITIES)
      .filter((key) => capabilityFleetStatus[key]?.hasAny || selected.includes(key));
    // Werte aus Altdaten, welche die Konfiguration nicht (mehr) kennt, dürfen
    // nicht unter den Tisch fallen — sonst verschwänden sie beim Speichern.
    selected.forEach((key) => { if (!keys.includes(key)) keys.push(key); });

    return keys.map((key) => {
      const { hasAny = false, isAvailable = false } = capabilityFleetStatus[key] || {};
      let hint = '';
      if (!hasAny) {
        hint = 'Kein Fahrzeug mit dieser Ausstattung angelegt';
      } else if (isSofortbestellung && !isAvailable) {
        hint = 'Derzeit kein Fahrzeug im Dienst';
      }
      return {
        key,
        label: SPECIAL_CAPABILITIES[key] || key,
        hint,
        unavailable: !!hint,
        disabled: !!hint && !selected.includes(key),
      };
    });
  }, [capabilityFleetStatus, formData.special_capabilities, formData.order_type]);

  // Für die Chips im zugeklappten Feld: gewählt, aber nicht bedienbar.
  const unavailableSelectedCapabilities = useMemo(
    () => new Set(specialCapabilityOptions.filter((o) => o.unavailable).map((o) => o.key)),
    [specialCapabilityOptions]
  );

  // Fahrzeug je Fahrer — aus der Vehicles-Sammlung, nicht aus dem Fahrer-Objekt.
  //
  // `users.car` (und damit `driver.car`) führt die Spezialfähigkeiten nicht mit,
  // nur vehicleId/model/licensePlate/vehicleType/paymentMethods. `driver.vehicle`
  // hätte sie zwar nach `fetchDrivers`, wird aber vom Fahrer-Listener mit einem
  // Kurzobjekt aus model/licensePlate überschrieben (driversSlice, flacher Merge)
  // — nach dem ersten Statuswechsel eines Fahrers wären sie also stillschweigend
  // weg. Die Fahrzeugsammlung ist die verlässliche Quelle; aus ihr liest auch die
  // Cloud Function.
  const vehicleByDriverId = useMemo(() => {
    const map = {};
    for (const vehicle of (vehicles || [])) {
      const driverId = vehicle.driverId || vehicle.current_driver?.id || vehicle.current_driver_id || null;
      if (driverId) map[driverId] = vehicle;
    }
    return map;
  }, [vehicles]);

  // Woran ein Fahrer an der Anforderung dieser Fahrt scheitert (leer = passt).
  //
  // Spiegelt `collectDriverRideMismatches` in functions/src/index.ts — dieselbe
  // Regel inklusive der beiden Sonderfälle: "kartenzahlung" verlangt kein
  // Ausstattungsmerkmal, sondern paymentMethods >= 2, und "kombi" gilt auch als
  // erfüllt, wenn das Fahrzeug ohnehin größer ist (Typ >= 2). Läuft die Regel hier
  // und dort auseinander, sperrt das Portal Fahrer, die die Vermittlung zulässt,
  // oder umgekehrt.
  const getDriverMismatchReason = useCallback((driver) => {
    if (!driver) return '';

    const vehicle = vehicleByDriverId[driver.id] || null;
    const fallback = driver.vehicle || driver.car || null;
    // Ohne jede Fahrzeuginformation ist kein Urteil möglich — bei Vorbestellungen
    // ist das der Normalfall (das Fahrzeug setzt die App erst bei Fahrtantritt).
    if (!vehicle && !fallback) return '';

    const driverVehicleType = parseInt(vehicle?.vehicleType ?? fallback?.vehicleType ?? 1, 10) || 1;
    const orderVehicleType = parseInt(formData.vehicle_type, 10) || 1;
    const reasons = [];

    if (!isVehicleCompatible(driverVehicleType, orderVehicleType)) {
      reasons.push(
        `Fahrzeug ist ${getVehicleTypeDescription(driverVehicleType)}, `
        + `gefordert ist ${getVehicleTypeDescription(orderVehicleType)}`
      );
    }

    const requested = formData.special_capabilities || [];
    if (requested.length > 0) {
      const caps = Array.isArray(vehicle?.specialCapabilities)
        ? vehicle.specialCapabilities.map(String)
        : [];
      const paymentMethods = parseInt(vehicle?.paymentMethods ?? fallback?.paymentMethods ?? 1, 10) || 1;
      const missing = requested.filter((capability) => {
        if (capability === 'kartenzahlung') return !(paymentMethods >= 2);
        if (capability === 'kombi') return !(caps.includes('kombi') || driverVehicleType >= 2);
        return !caps.includes(capability);
      });
      if (missing.length > 0) {
        reasons.push(`Fahrzeug ohne ${missing.map(c => SPECIAL_CAPABILITIES[c] || c).join(', ')}`);
      }
    }

    return reasons.join(' · ');
  }, [vehicleByDriverId, formData.vehicle_type, formData.special_capabilities]);

  // Sperren nur bei Sofortbestellungen: Dort fährt der Fahrer mit genau dem
  // Fahrzeug los, das jetzt an ihm hängt. Bei Vorbestellung und Dauerauftrag ist
  // es bis zum Termin offen — dort bleibt es beim Hinweis.
  const enforceDriverMatch = isAssignMode
    ? (currentRide?.orderType !== 1 && currentRide?.orderType !== 3)
    : (formData.order_type !== '1' && formData.order_type !== '3');

  // Der bereits zugewiesene Fahrer wird nie gesperrt — sonst ließe sich eine
  // Fahrt, die schon bei ihm liegt, im Formular nicht mehr speichern.
  const isDriverOptionDisabled = useCallback((driver) => (
    enforceDriverMatch
    && driver?.id !== currentRide?.driverId
    && !!getDriverMismatchReason(driver)
  ), [enforceDriverMatch, currentRide, getDriverMismatchReason]);

  // Passt der aktuell gewählte Fahrer nicht, sagt es eine Meldung unter dem Feld —
  // im zugeklappten Zustand ist die Zeile aus der Vorschlagsliste nicht sichtbar.
  const selectedDriverMismatch = useMemo(() => {
    if (!formData.driver_id) return '';
    const driver = (drivers || []).find(d => d.id === formData.driver_id);
    return driver ? getDriverMismatchReason(driver) : '';
  }, [drivers, formData.driver_id, getDriverMismatchReason]);

  /**
   * Verordnungen des oben gewählten Kunden.
   *
   * Ohne Kunden bleibt die Liste vollständig — dann ist noch nicht klar, für
   * wen die Fahrt ist. Steht der Kunde fest, sind alle übrigen Verordnungen
   * nur Fehlerquelle: Eine Fahrt auf die Verordnung eines anderen Patienten zu
   * buchen fällt erst bei der Abrechnung auf.
   *
   * Verknüpft wird über `customerId`. Ältere Verordnungen haben den noch nicht,
   * deshalb zusätzlich der Vergleich über den Patientennamen — sonst
   * verschwänden gerade die Altfälle aus der Auswahl.
   */
  const medicalOrdersForCustomer = useMemo(() => {
    const customerId = (formData.customer_id || '').trim();
    const customerName = (formData.customer_name || '').trim().toLowerCase();
    if (!customerId && !customerName) return medicalTransportOrders;

    const comparable = (value = '') => String(value)
      .toLowerCase()
      .replace(/[^a-zäöüß\s]/g, ' ')
      .split(/\s+/)
      .filter(part => part.length > 1)
      .sort()
      .join(' ');
    const wantedName = comparable(customerName);

    return medicalTransportOrders.filter((order) => {
      // Eine bereits gewählte Verordnung bleibt immer sichtbar — sonst fiele
      // sie beim Bearbeiten einer Fahrt aus der Liste, deren Kunde anders
      // heißt als der Patient, und die Auswahl würde sich selbst zurücksetzen.
      if (order.id === formData.medical_transport_order_id) return true;
      if (customerId && order.customerId === customerId) return true;
      if (wantedName && comparable(order.patientName) === wantedName) return true;
      return false;
    });
  }, [
    medicalTransportOrders,
    formData.customer_id,
    formData.customer_name,
    formData.medical_transport_order_id
  ]);

  const medicalOrdersFilteredByCustomer =
    medicalOrdersForCustomer.length !== medicalTransportOrders.length;

  const medicalTransportOrderCheck = useMemo(() => {
    if (!formData.is_krankenfahrt || !selectedMedicalTransportOrder) {
      return { severity: 'info', messages: [], plannedCount: 0, remainingRides: null };
    }

    const messages = [];
    let severity = 'success';
    const validFrom = startOfDay(selectedMedicalTransportOrder.validFrom);
    const validUntil = endOfDay(selectedMedicalTransportOrder.validUntil);
    const plannedCount = formData.order_type === '3'
      ? countRecurringDates(formData.recurring_start_date, formData.recurring_end_date, formData.recurring_weekdays)
      : 1;
    const maxRides = Number(selectedMedicalTransportOrder.maxRides) || 0;
    const usedRides = Number(selectedMedicalTransportOrder.usedRides) || 0;
    const alreadyPlannedRides = Number(selectedMedicalTransportOrder.plannedRides) || 0;
    const remainingRides = maxRides > 0 ? maxRides - usedRides - alreadyPlannedRides : null;

    const markWarning = () => {
      severity = severity === 'error' ? 'error' : 'warning';
    };

    const markError = () => {
      severity = 'error';
    };

    if (selectedMedicalTransportOrder.approvalRequired !== false &&
        selectedMedicalTransportOrder.approvalStatus !== 'approved') {
      markWarning();
      messages.push('Genehmigung ist noch nicht als genehmigt markiert.');
    }

    if (selectedMedicalTransportOrder.documentStatus === 'expired') {
      // „Abgelaufen" wird seit dem Ablauf-Scheduler tatsächlich gesetzt und ist
      // etwas anderes als „unvollständig". Bewusst nur ein Hinweis: Ob die
      // konkrete Fahrt außerhalb der Gültigkeit liegt, prüfen die beiden
      // Datumsvergleiche weiter unten — und die blockieren dann hart.
      markWarning();
      messages.push('Verordnung ist abgelaufen.');
    } else if (selectedMedicalTransportOrder.documentStatus !== 'complete') {
      markWarning();
      messages.push('Dokumentstatus ist noch nicht vollständig.');
    }

    if (remainingRides !== null && plannedCount > remainingRides) {
      markError();
      messages.push(`Kontingent reicht nicht aus: ${plannedCount} neue Fahrt(en), ${Math.max(remainingRides, 0)} verbleibend.`);
    }

    const firstRideDate = formData.order_type === '3'
      ? startOfDay(formData.recurring_start_date)
      : toDateOrNull(formData.scheduled_time) || new Date();
    const lastRideDate = formData.order_type === '3'
      ? endOfDay(formData.recurring_end_date)
      : toDateOrNull(formData.scheduled_time) || new Date();

    if (validFrom && firstRideDate && firstRideDate < validFrom) {
      markError();
      messages.push(`Fahrt liegt vor dem Verordnungsbeginn (${formatDate(selectedMedicalTransportOrder.validFrom)}).`);
    }

    if (validUntil && lastRideDate && lastRideDate > validUntil) {
      markError();
      messages.push(`Fahrt liegt nach dem Verordnungsende (${formatDate(selectedMedicalTransportOrder.validUntil)}).`);
    }

    if (messages.length === 0) {
      messages.push(`Verordnung passt zum Zeitraum. ${remainingRides === null ? 'Kein Kontingent hinterlegt.' : `${Math.max(remainingRides, 0)} Fahrt(en) verbleiben vor dieser Planung.`}`);
    }

    return { severity, messages, plannedCount, remainingRides };
  }, [
    formData.is_krankenfahrt,
    formData.order_type,
    formData.recurring_end_date,
    formData.recurring_start_date,
    formData.recurring_weekdays,
    formData.scheduled_time,
    selectedMedicalTransportOrder
  ]);

  // Bestätigung zurücksetzen, sobald kein harter Verordnungs-Hinweis mehr
  // vorliegt (Verordnung gewechselt, Datum/Wochentage korrigiert, Kranken-
  // fahrt abgewählt) — die Checkbox erscheint dann bewusst wieder unbestätigt.
  useEffect(() => {
    if (medicalTransportOrderCheck.severity !== 'error') {
      setMedicalOverrideAck(false);
    }
  }, [medicalTransportOrderCheck.severity]);

  // Baut den Übersteuerungs-Vermerk für die Fahrt (nur wenn ein harter Hinweis
  // aktiv ist und der Disponent ihn bestätigt hat) — sonst null. Wird an der
  // Fahrt gespeichert, damit über-kontingentierte/zeitraumverletzende
  // Buchungen nachvollziehbar bleiben.
  const buildMedicalWarningOverride = () => {
    if (!(formData.is_krankenfahrt &&
          selectedMedicalTransportOrder &&
          medicalTransportOrderCheck.severity === 'error' &&
          medicalOverrideAck)) {
      return null;
    }
    return {
      reason: medicalTransportOrderCheck.messages.join(' '),
      remainingRides: medicalTransportOrderCheck.remainingRides,
      plannedCount: medicalTransportOrderCheck.plannedCount,
      acknowledgedBy: currentUser?.uid || null,
      acknowledgedByName: currentUser?.name || currentUser?.displayName || null,
      acknowledgedAt: new Date().toISOString(),
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };

    // Spezialfähigkeiten zurücksetzen wenn Fahrzeugtyp wechselt
    if (name === 'vehicle_type') {
      updatedData.special_capabilities = [];
    }

    setFormData(updatedData);
    
    // Reset errors when field changes
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleMedicalTransportOrderChange = (event, order) => {
    // Schnellanlage-Option: keine bestehende Verordnung übernehmen — Patient
    // und Kundenkonto kommen beim Speichern automatisch aus dem Fahrt-Kunden,
    // die Kundenfelder bleiben deshalb unangetastet.
    if (order?.isCreatePlaceholder) {
      setCreateNewMedicalOrder(true);
      setFormData(prev => ({ ...prev, medical_transport_order_id: '' }));
      setFormErrors(prev => ({ ...prev, medical_transport_order_id: '' }));
      return;
    }

    setCreateNewMedicalOrder(false);
    setCreateNewCustomer(!order?.customerId && !!order?.patientName?.trim());

    setFormData(prev => {
      if (!order) {
        return {
          ...prev,
          medical_transport_order_id: ''
        };
      }

      const requiredCapabilities = Array.isArray(order.requiredCapabilities) ? order.requiredCapabilities : [];
      const nextCapabilities = [...new Set([...(prev.special_capabilities || []), ...requiredCapabilities])];

      return {
        ...prev,
        medical_transport_order_id: order.id,
        customer_id: order.customerId || '',
        customer_name: order.patientName || '',
        customer_phone: order.patientPhone || '',
        pickup_address: order.pickupAddress || prev.pickup_address,
        pickup_location: order.pickupLatitude
          ? {
              latitude: order.pickupLatitude || null,
              longitude: order.pickupLongitude || null
            }
          : prev.pickup_location,
        destination_address: order.destinationAddress || prev.destination_address,
        destination_location: order.destinationLatitude
          ? {
              latitude: order.destinationLatitude || null,
              longitude: order.destinationLongitude || null
            }
          : prev.destination_location,
        vehicle_type: String(order.defaultVehicleType || prev.vehicle_type || 1),
        special_capabilities: nextCapabilities,
        notes: order.defaultNotes || prev.notes || ''
      };
    });

    setFormErrors(prev => ({
      ...prev,
      medical_transport_order_id: '',
      customer_id: '',
      customer_name: '',
      pickup_address: '',
      destination_address: ''
    }));
  };

  // Schnellanlage (§8.1): minimale Platzhalter-Verordnung anlegen. Nur der
  // Patient (aus dem Fahrt-Kunden) wird gesetzt, documentStatus bleibt
  // 'incomplete' — Krankenkasse, Zeitraum, Kontingent usw. pflegt die
  // Zentrale nachträglich in der Verordnungsverwaltung nach.
  const createPlaceholderMedicalOrder = async (patientName, customerId) => {
    const resultAction = await dispatch(createMedicalTransportOrder({
      patientName: (patientName || '').trim(),
      customerId: customerId || null,
      patientPhone: formData.customer_phone || '',
      documentStatus: 'incomplete'
    }));
    if (!createMedicalTransportOrder.fulfilled.match(resultAction)) {
      throw new Error(resultAction.payload || 'Platzhalter-Verordnung konnte nicht angelegt werden.');
    }
    return resultAction.payload;
  };

  // Rechnungskunden-Schnellanlage (§8.4): legt über die Cloud Function
  // saveInvoiceCustomer (quickCreate) einen unvollständigen Firmenkunden an —
  // nur Firmenname; Anschrift/Rechnungs-E-Mail pflegt die Zentrale nach,
  // bis dahin ist die Rechnungserstellung serverseitig gesperrt.
  const createQuickInvoiceCustomer = async () => {
    const companyName = newInvoiceCustomerName.trim();
    const result = await httpsCallable(functions, 'saveInvoiceCustomer')({ companyName, quickCreate: true });
    return { id: result.data.id, name: companyName };
  };
  
  const handleOrderTypeChange = (e) => {
    const orderType = e.target.value;
    setFormData({
      ...formData,
      order_type: orderType,
      // Wenn Vorbestellung, aber kein Zeitpunkt gesetzt ist, setze einen Standardwert (1 Stunde in der Zukunft)
      scheduled_time: orderType === '1' && !formData.scheduled_time ?
        new Date(new Date().getTime() + 60 * 60 * 1000) : formData.scheduled_time,
      // Wenn Dauerauftrag, setze Standardwerte für wiederkehrende Termine
      recurring_start_date: orderType === '3' && !formData.recurring_start_date ?
        new Date(new Date().getTime() + 24 * 60 * 60 * 1000) : formData.recurring_start_date, // Morgen
      recurring_end_date: orderType === '3' && !formData.recurring_end_date ?
        new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000) : formData.recurring_end_date, // In 30 Tagen
      recurring_time: orderType === '3' && !formData.recurring_time ?
        new Date(new Date().setHours(8, 0, 0, 0)) : formData.recurring_time, // 08:00 Uhr
      // Dauerauftrag ist eine Vorlage – Fahrer/Fahrzeug werden pro generierter
      // Fahrt im Zuweisungsmodus vergeben, nicht auf der Vorlage selbst.
      driver_id: orderType === '3' ? '' : formData.driver_id,
      vehicle_id: orderType === '3' ? '' : formData.vehicle_id
    });
  };
  
  const handleScheduledTimeChange = (date) => {
    if (!date) {
      setFormData({
        ...formData,
        scheduled_time: null
      });
    } else {
      // Stelle sicher, dass das Datum im ISO-Format gespeichert wird
      const formattedDate = new Date(date);
      
      setFormData({
        ...formData,
        scheduled_time: formattedDate
      });
    }
    
    if (formErrors.scheduled_time) {
      setFormErrors({
        ...formErrors,
        scheduled_time: ''
      });
    }
  };
  
  // Handler für Dauerauftrag-Felder
  const handleRecurringStartDateChange = (date) => {
    setFormData({
      ...formData,
      recurring_start_date: date
    });
    
    if (formErrors.recurring_start_date) {
      setFormErrors({
        ...formErrors,
        recurring_start_date: ''
      });
    }
  };
  
  const handleRecurringEndDateChange = (date) => {
    setFormData({
      ...formData,
      recurring_end_date: date
    });
    
    if (formErrors.recurring_end_date) {
      setFormErrors({
        ...formErrors,
        recurring_end_date: ''
      });
    }
  };
  
  const handleRecurringTimeChange = (date) => {
    setFormData({
      ...formData,
      recurring_time: date
    });
    
    if (formErrors.recurring_time) {
      setFormErrors({
        ...formErrors,
        recurring_time: ''
      });
    }
  };
  
  const handleWeekdayChange = (dayIndex) => {
    const updatedWeekdays = formData.recurring_weekdays.includes(dayIndex)
      ? formData.recurring_weekdays.filter(day => day !== dayIndex)
      : [...formData.recurring_weekdays, dayIndex].sort();
    
    setFormData({
      ...formData,
      recurring_weekdays: updatedWeekdays
    });
    
    if (formErrors.recurring_weekdays) {
      setFormErrors({
        ...formErrors,
        recurring_weekdays: ''
      });
    }
  };
  
  const handleDriverChange = (event, driver) => {
    const updatedData = {
      ...formData,
      driver_id: driver ? driver.id : ''
    };

    // Fahrzeug automatisch vom Fahrer übernehmen (wenn vorhanden)
    if (driver && (driver.vehicle || driver.car)) {
      const vehicleId = driver.vehicle?.id || driver.car?.vehicleId || '';
      updatedData.vehicle_id = vehicleId;
    } else if (!driver) {
      updatedData.vehicle_id = '';
    }

    setFormData(updatedData);

    if (formErrors.driver_id || formErrors.vehicle_id) {
      setFormErrors({
        ...formErrors,
        driver_id: '',
        vehicle_id: ''
      });
    }
  };
  
  const handleVehicleChange = (event, vehicle) => {
    setFormData({
      ...formData,
      vehicle_id: vehicle ? vehicle.id : ''
    });
    
    if (formErrors.vehicle_id) {
      setFormErrors({
        ...formErrors,
        vehicle_id: ''
      });
    }
  };
  
  const handleCustomerChange = (event, newValue) => {
    if (typeof newValue === 'string') {
      // Wenn eine Texteingabe gemacht wurde (kein Kunde aus der Liste ausgewählt)
      setFormData({
        ...formData,
        customer_id: '',
        customer_name: newValue || ''
      });
      // Leerer String → kein neuer Kunde, sondern Laufkunde-Fallback
      setCreateNewCustomer(!!newValue && newValue.trim().length > 0);
    } else if (newValue && newValue.inputValue) {
      // Wenn "Neuen Kunden erstellen" ausgewählt wurde
      setFormData({
        ...formData,
        customer_id: '',
        customer_name: newValue.inputValue || ''
      });
      setCreateNewCustomer(true);
    } else {
      // Wenn ein existierender Kunde ausgewählt wurde
      setFormData({
        ...formData,
        customer_id: newValue?.id || '',
        customer_name: newValue?.name || newValue?.user?.name || '',
        customer_phone: newValue?.phone || newValue?.user?.phoneNumber || ''
      });
      setCreateNewCustomer(false);
    }
    
    if (formErrors.customer_id) {
      setFormErrors({
        ...formErrors,
        customer_id: ''
      });
    }
  };
  
  const handleCustomerPhoneChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      customer_phone: value
    });
    
    if (formErrors.customer_phone) {
      setFormErrors({
        ...formErrors,
        customer_phone: ''
      });
    }
  };

  const handleSwapAddresses = () => {
    setFormData(prev => ({
      ...prev,
      pickup_address: prev.destination_address,
      pickup_location: prev.destination_location,
      destination_address: prev.pickup_address,
      destination_location: prev.pickup_location
    }));
    setFormErrors(prev => ({
      ...prev,
      pickup_address: '',
      destination_address: ''
    }));
  };

  //
  // [waypoints] sind Zwischenstopps in anzufahrender Reihenfolge (ohne Endziel).
  // Sie gehen als `waypoints` in EINE Anfrage; Distanz und Dauer sind dann die
  // Summe aller Etappen, und die Etappenwerte kommen als `legs` zurück
  // (zwischenstopps_konzept.md §5.2). Ohne Zwischenstopps ist das Verhalten
  // unverändert: genau ein Leg.
  const calculateEstimatedTimeMinutes = async (pickupLocation, destinationLocation, departureTime = null, waypoints = []) => {
    if (!pickupLocation || !destinationLocation || 
        !pickupLocation.latitude || !pickupLocation.longitude ||
        !destinationLocation.latitude || !destinationLocation.longitude) {
      console.warn('Fehlende Koordinaten für Fahrzeitberechnung');
      return null;
    }

    try {
      const directionsService = new window.google.maps.DirectionsService();
      
      const request = {
        origin: { lat: pickupLocation.latitude, lng: pickupLocation.longitude },
        destination: { lat: destinationLocation.latitude, lng: destinationLocation.longitude },
        travelMode: window.google.maps.TravelMode.DRIVING,
        // Reihenfolge NICHT optimieren lassen: Sie ist vom Disponenten bzw.
        // Fahrgast bewusst gesetzt (§3.1).
        ...(waypoints.length > 0
          ? {
              waypoints: waypoints.map((w) => ({
                location: { lat: w.latitude, lng: w.longitude },
                stopover: true,
              })),
              optimizeWaypoints: false,
            }
          : {}),
        drivingOptions: {
          departureTime: departureTime || new Date(),
          trafficModel: window.google.maps.TrafficModel.BEST_GUESS
        }
      };

      const result = await new Promise((resolve, reject) => {
        directionsService.route(request, (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            resolve(result);
          } else {
            reject(new Error(`Directions API Fehler: ${status}`));
          }
        });
      });

      // Geschätzte Fahrtdauer über ALLE Etappen (mit Verkehr wenn verfügbar).
      const route = result.routes[0];
      if (route && route.legs && route.legs[0]) {
        const legSeconds = (leg) =>
          // duration_in_traffic ist nur verfügbar wenn departureTime gesetzt ist
          leg.duration_in_traffic ? leg.duration_in_traffic.value : leg.duration.value;

        const legs = route.legs.map((leg) => ({
          durationMin: Math.ceil(legSeconds(leg) / 60),
          distanceKm: leg.distance ? Number((leg.distance.value / 1000).toFixed(2)) : null,
        }));

        const durationInMinutes = Math.ceil(
          route.legs.reduce((sum, leg) => sum + legSeconds(leg), 0) / 60,
        );
        const distanceKm = route.legs.reduce(
          (sum, leg) => sum + (leg.distance ? leg.distance.value / 1000 : 0),
          0,
        ) || null;

        console.log('Berechnete Fahrtdauer:', {
          route: `${pickupLocation.latitude},${pickupLocation.longitude} -> ${destinationLocation.latitude},${destinationLocation.longitude}`,
          zwischenstopps: waypoints.length,
          durationInMinutes,
          distanceKm,
          withTraffic: !!route.legs[0].duration_in_traffic,
          departureTime: departureTime || 'Jetzt'
        });

        return { durationInMinutes, distanceKm, legs };
      }

      return null;
    } catch (error) {
      console.error('Fehler bei der Fahrzeitberechnung:', error);
      return null;
    }
  };

  // ---- Preisschätzung (Tarif + Anfahrtskosten) -----------------------------
  // Gegenstücke: home_customer.dart (App) und getFareEstimate/createRide (CF).
  // Konzept: docs/general/anfahrtspreis_konzept.md (App-Repo).

  const coordsOf = (location) => {
    const lat = location?.latitude;
    const lng = location?.longitude;
    if (typeof lat === 'number' && typeof lng === 'number') return { lat, lng };
    return null;
  };

  // Positionen, mit denen gerechnet wird. Schätzung und Anzeige nehmen die
  // Formularwerte; der Submit reicht stattdessen die aufgelösten Koordinaten
  // durch (siehe resolveFormCoordinates).
  const formLocations = () => ({
    pickup: formData.pickup_location,
    destination: formData.destination_location,
    stops: formData.stops || [],
  });

  // Adressvorschläge liefern Koordinaten frei Haus — getippte Adressen nicht.
  // Vor dem Speichern deshalb nachgeocodieren, sonst entstehen Fahrten mit
  // (0,0): ohne Route auf der LiveMap und ohne belastbare Schätzung.
  // Die Zieladresse ist optional (Laufkunden ohne Ziel), die Abholadresse nicht.
  const resolveFormCoordinates = async () => {
    const bounds = getServiceAreaBias(globalSettings)?.bounds || null;
    const resolved = { ...formLocations() };

    const pickupAddress = (formData.pickup_address || '').trim();
    if (pickupAddress && !hasCoordinates(resolved.pickup)) {
      const geocoded = await geocodeAddressToLocation(pickupAddress, { bounds });
      if (!geocoded) {
        return {
          ok: false,
          field: 'pickup_address',
          message: 'Abholadresse konnte nicht zugeordnet werden — bitte einen Vorschlag aus der Liste wählen.',
        };
      }
      resolved.pickup = { latitude: geocoded.latitude, longitude: geocoded.longitude };
    }

    const destinationAddress = (formData.destination_address || '').trim();
    if (destinationAddress && !hasCoordinates(resolved.destination)) {
      const geocoded = await geocodeAddressToLocation(destinationAddress, { bounds });
      if (!geocoded) {
        return {
          ok: false,
          field: 'destination_address',
          message: 'Zieladresse konnte nicht zugeordnet werden — bitte einen Vorschlag aus der Liste wählen.',
        };
      }
      resolved.destination = { latitude: geocoded.latitude, longitude: geocoded.longitude };
    }

    // Zwischenstopps genauso behandeln: Ein Halt ohne echte Koordinaten würde
    // die Route verbiegen und als (0,0) in der Fahrt landen.
    const resolvedStops = [];
    for (const [i, stop] of (formData.stops || []).entries()) {
      const address = (stop.address || '').trim();
      if (!address) continue;
      let location = stop.location;
      if (!hasCoordinates(location)) {
        const geocoded = await geocodeAddressToLocation(address, { bounds });
        if (!geocoded) {
          return {
            ok: false,
            field: `stop_${i}`,
            message: `Zwischenstopp ${i + 1} konnte nicht zugeordnet werden — bitte einen Vorschlag aus der Liste wählen.`,
          };
        }
        location = { latitude: geocoded.latitude, longitude: geocoded.longitude };
      }
      resolvedStops.push({ address, location });
    }
    resolved.stops = resolvedStops;

    // Nachgeocodierte Werte ins Formular übernehmen, damit die Anzeige (und ein
    // zweiter Speicherversuch) denselben Stand hat wie die gespeicherte Fahrt.
    setFormData(prev => ({
      ...prev,
      pickup_location: resolved.pickup,
      destination_location: resolved.destination,
      stops: resolvedStops,
    }));

    return { ok: true, locations: resolved };
  };

  // Routen-Distanz (Meter) OHNE Verkehr/departureTime → günstiger Basic-SKU
  // (Anfahrtsstrecke braucht keine verkehrsabhängige Dauer). null bei Fehler.
  const routeDistanceMetersNoTraffic = async (origin, destination) => {
    if (!window.google?.maps) return null;
    try {
      const directionsService = new window.google.maps.DirectionsService();
      const result = await new Promise((resolve, reject) => {
        directionsService.route({
          origin: { lat: origin.lat, lng: origin.lng },
          destination: { lat: destination.lat, lng: destination.lng },
          travelMode: window.google.maps.TravelMode.DRIVING,
        }, (res, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) resolve(res);
          else reject(new Error(`Directions API Fehler: ${status}`));
        });
      });
      const leg = result.routes?.[0]?.legs?.[0];
      return leg?.distance?.value ?? null;
    } catch (error) {
      console.warn('Anfahrts-Routendistanz fehlgeschlagen:', error?.message);
      return null;
    }
  };

  // Anfahrtskosten für die aktuellen Formulardaten oder null (Gating: Feature
  // aktiv, kein Festpreis, keine Krankenfahrt, Abholung UND Ziel außerhalb).
  const computeAnfahrtForForm = async (locations = formLocations()) => {
    if (globalSettings?.anfahrtEnabled !== true) return null;
    if (formData.is_festpreis && formData.festpreis_eur) return null;
    if (formData.is_krankenfahrt) return null;
    const pickup = coordsOf(locations.pickup);
    const destination = coordsOf(locations.destination);
    if (!pickup || !destination) return null;
    if (!shouldChargeAnfahrt(globalSettings, pickup, destination)) return null;
    const boundary = nearestBoundaryPoint(globalSettings, pickup);
    if (!boundary) return null;

    let meters = await routeDistanceMetersNoTraffic(boundary, pickup);
    let viaRoute = true;
    if (meters === null || meters <= 0) {
      viaRoute = false;
      meters = haversineMeters(pickup, boundary); // Luftlinien-Fallback
    }
    const result = calcAnfahrtFromMeters(meters, effectiveAnfahrtPreisProKm(globalSettings));
    if (!result) return null;
    return { ...result, viaRoute, note: buildAnfahrtNote(result) };
  };

  // Bindet ein Schätzergebnis an die relevanten Eingaben. Ändert sich etwas,
  // wird neu gerechnet; sonst nutzt der Submit den Cache aus fareEstimateRef.
  const estimateKey = (locations = formLocations()) => {
    const p = coordsOf(locations.pickup);
    const d = coordsOf(locations.destination);
    const when = formData.order_type === '1' && formData.scheduled_time
      ? new Date(formData.scheduled_time).toISOString()
      : 'now';
    // Die Halte gehören in den Schlüssel: sonst käme nach dem Ergänzen eines
    // Zwischenstopps die Schätzung der alten Strecke aus dem Cache.
    const via = (locations.stops || [])
      .map((s) => coordsOf(s.location))
      .filter(Boolean)
      .map((c) => `${c.lat},${c.lng}`)
      .join('>') || '-';
    return [
      p ? `${p.lat},${p.lng}` : '-',
      via,
      d ? `${d.lat},${d.lng}` : '-',
      formData.vehicle_type,
      formData.order_type,
      when,
      formData.is_festpreis ? `fp:${formData.festpreis_eur}` : 'nofp',
      formData.is_krankenfahrt ? 'kf' : '-',
      // Spezialfähigkeiten steuern den Zuschlag → gehören in den Cache-Key.
      [...(formData.special_capabilities || [])].sort().join(',') || '-',
    ].join('|');
  };

  // Berechnet Distanz, Fahrpreis (Tarif oder Festpreis) und Anfahrt für die
  // aktuellen Eingaben. dateTime steuert den Nachttarif (Vorbestellung → geplante
  // Zeit). Rückgabe wird sowohl angezeigt als auch beim Submit persistiert.
  const buildEstimate = async (locations = formLocations()) => {
    const key = estimateKey(locations);
    const pickup = coordsOf(locations.pickup);
    const destination = coordsOf(locations.destination);
    const vehicleType = parseInt(formData.vehicle_type, 10) || 1;
    const dateTime = formData.order_type === '1' && formData.scheduled_time
      ? new Date(formData.scheduled_time)
      : new Date();

    let distanceKm = null;
    let durationInMinutes = null;
    let legs = null;
    if (pickup && destination) {
      const waypoints = (locations.stops || [])
        .map((s) => s.location)
        .filter((loc) => coordsOf(loc));
      const routeResult = await calculateEstimatedTimeMinutes(
        locations.pickup,
        locations.destination,
        formData.order_type === '1' && formData.scheduled_time ? formData.scheduled_time : null,
        waypoints
      );
      if (routeResult) {
        distanceKm = routeResult.distanceKm;
        durationInMinutes = routeResult.durationInMinutes;
        legs = routeResult.legs || null;
      }
    }

    const isFestpreis = !!(formData.is_festpreis && formData.festpreis_eur);
    const capabilities = formData.special_capabilities || [];
    // Spezialzuschlag (Rollstuhl/Tragestuhl/Liegend) — verdrängt den
    // Großraumzuschlag, entfällt bei Verordnungsfahrten.
    // Siehe nextcab_mobile/docs/general/spezialzuschlaege_konzept.md §4.
    //
    // Ein hier von Hand eingegebener Festpreis ist der Endbetrag, den der
    // Disponent nennt — der Zuschlag gilt als darin enthalten und wird deshalb
    // wie ein abdeckender Festpreis behandelt: Betrag 0, aber mit Grund. Nur so
    // rechnet die Fahrer-App denselben Preis (sie würde ihn sonst aus den
    // specialCapabilities nachträglich aufschlagen).
    const zuschlag = spezialZuschlag(globalSettings, {
      capabilities,
      isKrankenfahrt: !!formData.is_krankenfahrt,
      festpreisCapabilities: isFestpreis ? capabilities : undefined,
    });
    // Festpreis: eingegebener Betrag (Anfahrt ist darin einkalkuliert → keine).
    // Sonst Taxameter-Tarif; Anfahrt kommt separat obendrauf.
    const anfahrt = isFestpreis ? null : await computeAnfahrtForForm(locations);
    const baseFareEur = isFestpreis
      ? parseFloat(formData.festpreis_eur)
      : calculateEstimatedFareEur(globalSettings, {
          distanceKm,
          vehicleType,
          dateTime,
          capabilities,
          isKrankenfahrt: !!formData.is_krankenfahrt,
        });

    const totalFareEur = baseFareEur === null || baseFareEur === undefined
      ? null
      : Math.round((baseFareEur + (anfahrt?.eur ?? 0)) * 100) / 100;

    return {
      key,
      distanceKm,
      durationInMinutes,
      // Etappenwerte je Halt (nur bei Mehr-Halte-Fahrten gefüllt) — werden
      // beim Speichern in die einzelnen Halte geschrieben.
      legs,
      isFestpreis,
      baseFareEur: baseFareEur ?? null,
      anfahrt,
      zuschlag,
      totalFareEur,
      nightTariff: !isFestpreis && isNachtTarif(globalSettings, dateTime),
    };
  };

  // Liefert die Schätzung für den Submit: den bereits berechneten Cache-Wert,
  // wenn die Eingaben unverändert sind, sonst frisch (spart Directions-Calls).
  const resolveEstimate = async (locations = formLocations()) => {
    const cached = fareEstimateRef.current;
    if (cached && cached.key === estimateKey(locations)) return cached;
    return buildEstimate(locations);
  };

  // Baut das notes-Feld: Capabilities-Prefix + Benutzernotiz + optional die
  // Anfahrts-Notiz. Eine evtl. schon enthaltene Anfahrts-Notiz wird zuvor
  // entfernt, damit sie beim erneuten Bearbeiten nicht doppelt entsteht.
  const buildRideNotes = (anfahrtNote) => {
    const userNotes = (stripAnfahrtNote(formData.notes) || '').trim();
    let base;
    if (formData.special_capabilities?.length > 0) {
      const capsStr = formData.special_capabilities
        .map(c => SPECIAL_CAPABILITIES[c] || c)
        .join(', ');
      base = userNotes ? `[${capsStr}] ${userNotes}` : `[${capsStr}]`;
    } else {
      base = userNotes;
    }
    if (anfahrtNote) base = base ? `${base} · ${anfahrtNote}` : anfahrtNote;
    return base && base.length > 0 ? base : null;
  };

  // Debounced Live-Schätzung für die Anzeige im Formular.
  useEffect(() => {
    let cancelled = false;
    const hasPickup = coordsOf(formData.pickup_location);
    const hasDest = coordsOf(formData.destination_location);
    const isFestpreis = !!(formData.is_festpreis && formData.festpreis_eur);
    // Ohne Preis-Basis (weder Route noch Festpreis) nichts anzeigen.
    if (!isFestpreis && !(hasPickup && hasDest)) {
      setFareEstimate(null);
      fareEstimateRef.current = null;
      return undefined;
    }
    const timer = setTimeout(async () => {
      setIsCalculatingFare(true);
      try {
        const est = await buildEstimate();
        if (cancelled) return;
        setFareEstimate(est);
        fareEstimateRef.current = est;
      } finally {
        if (!cancelled) setIsCalculatingFare(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.pickup_location, formData.destination_location, formData.vehicle_type,
    formData.order_type, formData.scheduled_time, formData.is_festpreis,
    formData.festpreis_eur, formData.is_krankenfahrt, globalSettings,
  ]);

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    newErrors.medical_transport_order_id = '';
    newErrors.invoice_customer_id = '';

    // Rechnungsfahrt benötigt einen Rechnungskunden — bestehend ausgewählt
    // oder als Schnellanlage (neuer Firmenname) vorgemerkt (§8.4).
    if (formData.is_rechnungsfahrt && !formData.invoice_customer_id && !newInvoiceCustomerName.trim()) {
      newErrors.invoice_customer_id = 'Bitte einen Rechnungskunden auswählen oder einen neuen Firmennamen eingeben';
      valid = false;
    }

    // Sicherheitsnetz: Kranken- und Firmen-Rechnungsfahrt schließen sich aus
    // (getrennte Abrechnungswege, rechnungskunden_konzept §2). Die Checkboxen
    // deaktivieren sich gegenseitig — hier nur der Schutz gegen alte
    // Duplikate/Edits, bei denen beide Flags gesetzt sein könnten.
    if (formData.is_rechnungsfahrt && formData.is_krankenfahrt) {
      newErrors.invoice_customer_id =
        'Krankenfahrt und Rechnungsfahrt schließen sich aus — bitte nur eines wählen';
      valid = false;
    }

    if (!isAssignMode) {
      // Kundenfeld ist optional: Bleibt es leer, wird automatisch ein "Laufkunde"
      // (Telefonkunde ohne eigenen Datensatz) als Kunde der Fahrt verwendet.
      // Wenn ein neuer Telefonkunde erstellt werden soll, muss ein Name angegeben sein.
      if (createNewCustomer && !formData.customer_name.trim()) {
        newErrors.customer_name = 'Bitte geben Sie einen Namen für den neuen Telefonkunden ein';
        valid = false;
      }
      
      if (!formData.pickup_address) {
        newErrors.pickup_address = 'Abholadresse ist erforderlich';
        valid = false;
      }
      
      // Zieladresse ist optional, aber ein Hinweis wird im UI angezeigt
      
      // Validiere scheduledTime für Vorbestellungen
      if (formData.order_type === '1' && !formData.scheduled_time) {
        newErrors.scheduled_time = 'Bitte geben Sie einen Zeitpunkt für die Vorbestellung an';
        valid = false;
      }
      
      if (formData.order_type === '1' && formData.scheduled_time && new Date(formData.scheduled_time) < new Date()) {
        newErrors.scheduled_time = 'Der Zeitpunkt muss in der Zukunft liegen';
        valid = false;
      }
      
      // Validiere Dauerauftrag-Felder
      if (formData.order_type === '3') {
        if (!formData.recurring_start_date) {
          newErrors.recurring_start_date = 'Bitte geben Sie ein Startdatum für den Dauerauftrag an';
          valid = false;
        }
        
        if (!formData.recurring_end_date) {
          newErrors.recurring_end_date = 'Bitte geben Sie ein Enddatum für den Dauerauftrag an';
          valid = false;
        }
        
        if (!formData.recurring_time) {
          newErrors.recurring_time = 'Bitte geben Sie eine Uhrzeit für den Dauerauftrag an';
          valid = false;
        }
        
        if (formData.recurring_weekdays.length === 0) {
          newErrors.recurring_weekdays = 'Bitte wählen Sie mindestens einen Wochentag aus';
          valid = false;
        }
        
        if (formData.recurring_start_date && formData.recurring_end_date && 
            new Date(formData.recurring_end_date) <= new Date(formData.recurring_start_date)) {
          newErrors.recurring_end_date = 'Das Enddatum muss nach dem Startdatum liegen';
          valid = false;
        }
        
        if (formData.recurring_start_date && new Date(formData.recurring_start_date) < new Date()) {
          newErrors.recurring_start_date = 'Das Startdatum muss in der Zukunft liegen';
          valid = false;
        }
      }

      // Für Sofortbestellungen mit Direktzuweisung: Fahrer muss ein Fahrzeug haben
      if (formData.driver_id && formData.order_type !== '1' && formData.order_type !== '3') {
        const selectedDriver = drivers.find(d => d.id === formData.driver_id);
        if (selectedDriver && !selectedDriver.vehicle && !selectedDriver.car) {
          newErrors.driver_id = 'Diesem Fahrer ist kein Fahrzeug zugewiesen. Für Sofortbestellungen muss ein Fahrzeug vorhanden sein.';
          valid = false;
        }
      }

      // Harte Verordnungs-Hinweise (Kontingent überschritten / Fahrt außerhalb
      // des Verordnungszeitraums) blockieren nicht mehr per se — der Disponent
      // kann sie mit der Bestätigungs-Checkbox bewusst übersteuern. Ohne
      // Bestätigung bleibt der Submit gesperrt.
      if (formData.is_krankenfahrt && selectedMedicalTransportOrder &&
          medicalTransportOrderCheck.severity === 'error' && !medicalOverrideAck) {
        newErrors.medical_transport_order_id = medicalTransportOrderCheck.messages.join(' ')
          + ' — zum Fortfahren bitte unten die Bestätigung ankreuzen.';
        valid = false;
      }

      // Verordnungspflicht (Verordnungspflicht-Konzept §3): Krankenfahrt nur
      // mit ausgewählter Verordnung oder gewählter Schnellanlage — analog zur
      // Rechnungsfahrt-Regel oben. Privatzahler ohne Kassen-Verordnung sind
      // fachlich keine Krankenfahrt (normale Fahrt mit Quittung bzw.
      // Rechnungsfahrt mit Rechnungskunde).
      if (formData.is_krankenfahrt && !formData.medical_transport_order_id && !createNewMedicalOrder) {
        newErrors.medical_transport_order_id =
          'Pflichtfeld: Verordnung auswählen oder „Neue Verordnung anlegen" nutzen. ' +
          'Privatversicherte ohne Kassen-Verordnung bitte als normale Fahrt (Quittung) ' +
          'oder Rechnungsfahrt mit Rechnungskunde anlegen.';
        valid = false;
      }

      // Die Schnellanlage übernimmt den Patienten aus dem Fahrt-Kunden — ohne
      // Kundennamen (Laufkunde) lässt sich keine sinnvolle Verordnung anlegen.
      if (formData.is_krankenfahrt && createNewMedicalOrder && !formData.customer_name.trim()) {
        newErrors.medical_transport_order_id =
          'Für die Verordnungs-Schnellanlage wird der Fahrt-Kunde als Patient übernommen — ' +
          'bitte zuerst einen Kunden auswählen oder dessen Namen eingeben.';
        valid = false;
      }
    } else {
      // Validate ride assignment fields
      if (!formData.driver_id) {
        newErrors.driver_id = 'Bitte wählen Sie einen Fahrer aus';
        valid = false;
      } else if (currentRide?.orderType !== 1 && currentRide?.orderType !== 3) {
        // Für Sofortbestellungen: Fahrer muss ein Fahrzeug zugewiesen haben
        const selectedDriver = drivers.find(d => d.id === formData.driver_id);
        if (selectedDriver && !selectedDriver.vehicle && !selectedDriver.car) {
          newErrors.driver_id = 'Diesem Fahrer ist kein Fahrzeug zugewiesen. Für Sofortbestellungen muss ein Fahrzeug vorhanden sein.';
          valid = false;
        }
      }

      // Fahrzeug nur bei Sofortbestellungen als Pflichtfeld prüfen
      if (currentRide?.orderType !== 1 && currentRide?.orderType !== 3 && !formData.vehicle_id) {
        newErrors.vehicle_id = 'Bitte wählen Sie ein Fahrzeug aus';
        valid = false;
      }
    }
    
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleTemplateSelect = (event, template) => {
    if (!template) return;
    
    setFormData(prevData => ({
      ...prevData,
      customer_name: template.customer_name,
      customer_phone: template.customer_phone || '',
      customer_id: template.customer_id || '',
      pickup_address: template.pickup_address,
      pickup_location: template.pickup_location || { latitude: null, longitude: null },
      destination_address: template.destination_address,
      destination_location: template.destination_location || { latitude: null, longitude: null },
      vehicle_type: template.vehicle_type,
      order_type: template.order_type || '2'
    }));
  };

  // "Nur als Vorlage anlegen": speichert die aktuellen Eingaben als Vorlage für den
  // gewählten Kunden, OHNE eine Fahrt anzulegen. Nur mit echtem Kunden + Abholung + Ziel.
  // Nur Vorlagen des aktuell gewählten (echten) Kunden anbieten.
  const customerTemplates = useMemo(
    () => (formData.customer_id ? (templates || []).filter(t => t.customer_id === formData.customer_id) : []),
    [templates, formData.customer_id]
  );

  const handleSaveTemplateOnly = () => {
    if (!formData.customer_id || !formData.pickup_address || !formData.destination_address || !formData.vehicle_type) {
      return;
    }
    dispatch(saveRideTemplate({
      customer_name: formData.customer_name,
      customer_phone: formData.customer_phone,
      customer_id: formData.customer_id,
      pickup_address: formData.pickup_address,
      pickup_location: formData.pickup_location,
      destination_address: formData.destination_address,
      destination_location: formData.destination_location,
      vehicle_type: formData.vehicle_type,
      order_type: formData.order_type
    })).then((result) => {
      if (!result.error) {
        onClose?.();
      }
    });
  };

  // Funktion zum Generieren aller Einzelfahrten aus einem Dauerauftrag
  const generateRecurringRides = (recurringOrderData) => {
    const rides = [];
    const startDate = new Date(formData.recurring_start_date);
    const endDate = new Date(formData.recurring_end_date);
    const selectedWeekdays = formData.recurring_weekdays;
    
    // Generiere eine eindeutige ID für alle Fahrten dieses Dauerauftrags
    const recurringOrderId = `recurring_${Date.now()}`;
    
    // Setze die Zeit auf den Start des Tages für korrekte Vergleiche
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    const current = new Date(startDate);
    
    while (current <= endDate) {
      const currentWeekday = current.getDay();
      
      if (selectedWeekdays.includes(currentWeekday)) {
        // Erstelle die scheduledTime für diesen Tag
        const rideDateTime = new Date(current);
        const timeOnly = new Date(formData.recurring_time);
        rideDateTime.setHours(timeOnly.getHours(), timeOnly.getMinutes(), 0, 0);
        
        // Erstelle Fahrtdaten für diesen Termin
        const rideData = {
          ...recurringOrderData,
          scheduledTime: rideDateTime.toISOString(),
          status: 'scheduled', // Wiederkehrende Fahrten sind immer geplant
          orderType: 1, // Behandle wie Vorbestellung
          recurringOrderId: recurringOrderId, // Verwende die gemeinsame ID für alle Fahrten
          isRecurring: true,
          // Daueraufträge tragen in Phase 1 keine Zwischenstopps
          // (zwischenstopps_konzept.md §10.2). Die Felder werden hier
          // ausdrücklich geleert statt weggelassen: commonRideData wird
          // gespreizt und brächte sie sonst mit.
          stops: null,
          stopsTotal: null,
          currentStopIndex: null,
        };
        
        rides.push(rideData);
      }
      
      // Gehe zum nächsten Tag
      current.setDate(current.getDate() + 1);
    }
    
    return rides;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (validateForm()) {
      if (isAssignMode) {
        // Assign driver and vehicle to ride
        dispatch(assignRide({
          rideId,
          driverId: formData.driver_id
        })).then((result) => {
          if (!result.error) {
            onSuccess?.(result.payload);
            onClose?.();
          }
        });
      } else if (isEditMode) {
        // Getippte Adressen ohne Vorschlagsauswahl zuerst nachgeocodieren, damit
        // Schätzung und gespeicherte Fahrt dieselben Koordinaten tragen.
        setIsCalculatingRoute(true);
        let estimate;
        let locations;
        try {
          const resolvedCoordinates = await resolveFormCoordinates();
          if (!resolvedCoordinates.ok) {
            setFormErrors(prev => ({ ...prev, [resolvedCoordinates.field]: resolvedCoordinates.message }));
            return;
          }
          locations = resolvedCoordinates.locations;
          estimate = await resolveEstimate(locations);
        } finally {
          setIsCalculatingRoute(false);
        }
        const estimatedTimeMinutes = estimate.durationInMinutes;
        const distanceKm = estimate.distanceKm;
        const anfahrt = estimate.anfahrt;

        // Schnellanlage: Platzhalter-Verordnung zuerst anlegen, damit die
        // Fahrt sofort eine gültige medicalTransportOrderId trägt (§8.1).
        let medicalOrderIdForSave = formData.is_krankenfahrt ? (formData.medical_transport_order_id || null) : null;
        let medicalOrderForSnapshot = selectedMedicalTransportOrder;
        if (formData.is_krankenfahrt && createNewMedicalOrder && !medicalOrderIdForSave) {
          try {
            const placeholderOrder = await createPlaceholderMedicalOrder(formData.customer_name, formData.customer_id);
            medicalOrderIdForSave = placeholderOrder.id;
            medicalOrderForSnapshot = placeholderOrder;
            setCreateNewMedicalOrder(false);
          } catch (placeholderError) {
            setFormErrors(prev => ({ ...prev, medical_transport_order_id: placeholderError.message }));
            return;
          }
        }

        const medicalTransportSnapshot = formData.is_krankenfahrt
          ? buildMedicalTransportSnapshot(medicalOrderForSnapshot)
          : null;

        // Rechnungskunden-Schnellanlage: neuen (unvollständigen) Firmenkunden
        // zuerst anlegen, damit die Fahrt eine gültige invoiceCustomerId trägt.
        // Bei bereits abgerechneten Fahrten ('billed') bleiben die Felder
        // ohnehin unangetastet — dann auch keinen Kunden anlegen.
        let invoiceCustomerIdForSave = (formData.is_rechnungsfahrt && formData.invoice_customer_id) ? formData.invoice_customer_id : null;
        let invoiceCustomerNameForSave = invoiceCustomerIdForSave
          ? (invoiceCustomers.find((c) => c.id === invoiceCustomerIdForSave)?.displayName
             || invoiceCustomers.find((c) => c.id === invoiceCustomerIdForSave)?.companyName || null)
          : null;
        if (formData.is_rechnungsfahrt && !invoiceCustomerIdForSave && newInvoiceCustomerName.trim()
            && currentRide.invoiceBillingStatus !== 'billed') {
          try {
            const quickCustomer = await createQuickInvoiceCustomer();
            invoiceCustomerIdForSave = quickCustomer.id;
            invoiceCustomerNameForSave = quickCustomer.name;
            setNewInvoiceCustomerName('');
          } catch (quickError) {
            setFormErrors(prev => ({ ...prev, invoice_customer_id: quickError.message || 'Rechnungskunde konnte nicht angelegt werden.' }));
            return;
          }
        }

        // Fahrt aktualisieren mit aktuellen Formular-Daten
        const updateData = {
          rideId,
          // Grundlegende Felder
          start: formData.pickup_address,
          startLatitude: locations.pickup?.latitude || 0,
          startLongitude: locations.pickup?.longitude || 0,
          destination: formData.destination_address,
          destinationLatitude: locations.destination?.latitude || 0,
          destinationLongitude: locations.destination?.longitude || 0,
          // Zwischenstopps (§2.1); beim Entfernen des letzten Halts werden die
          // Felder ausdrücklich auf null gesetzt, nicht weggelassen.
          ...buildStopFields({
            intermediateStops: (locations.stops || []).map((s) => ({
              address: s.address,
              latitude: s.location?.latitude,
              longitude: s.location?.longitude,
            })),
            destinationAddress: formData.destination_address,
            destinationLocation: locations.destination,
            legs: estimate.legs,
          }),
          vehicleType: parseInt(formData.vehicle_type, 10),
          specialCapabilities: formData.special_capabilities || [],
          notes: buildRideNotes(anfahrt?.note),
          isKrankenfahrt: formData.is_krankenfahrt,
          medicalTransportOrderId: medicalOrderIdForSave,
          medicalTransportSnapshot,
          medicalTransportBillingStatus: formData.is_krankenfahrt ? 'not_ready' : null,
          medicalTransportProofStatus: formData.is_krankenfahrt ? 'missing' : null,
          // Bewusste Übersteuerung eines harten Verordnungs-Hinweises (oder null).
          medicalTransportWarningOverride: buildMedicalWarningOverride(),
          // Rechnungsfahrt-Zuordnung: bereits abgerechnete Fahrten ('billed')
          // bleiben unangetastet (Korrektur nur über Storno, Konzept 8.4).
          // Sonst den Abrechnungsstatus ERHALTEN, solange der Rechnungskunde
          // gleich bleibt — ein Edit darf 'ready' nicht auf 'pending'
          // zurücksetzen, sonst wird die Fahrt nie abgerechnet. paymentMethod
          // setzt die Cloud Function (Rules sperren das Feld clientseitig).
          ...(currentRide.invoiceBillingStatus === 'billed' ? {} : {
            isRechnungsfahrt: formData.is_rechnungsfahrt,
            invoiceCustomerId: invoiceCustomerIdForSave,
            invoiceCustomerName: invoiceCustomerNameForSave,
            invoiceBillingStatus: !invoiceCustomerIdForSave
              ? null
              : (currentRide.invoiceCustomerId === invoiceCustomerIdForSave && currentRide.invoiceBillingStatus)
                ? currentRide.invoiceBillingStatus
                : 'pending',
          }),
          isFestpreis: formData.is_festpreis && formData.festpreis_eur ? true : false,
          // Anfahrtskosten: neu bewerten (kann durch Adress-/Tarifänderung ent-
          // fallen), daher immer setzen — bei keiner Gebühr auf null.
          anfahrtEur: anfahrt ? anfahrt.eur : null,
          anfahrtKm: anfahrt ? anfahrt.km : null,
          // Spezialzuschlag ebenso neu bewerten (Fähigkeit oder Fahrtart kann
          // sich geändert haben) — spezialzuschlaege_konzept.md §3.2.
          spezialZuschlagEur: estimate.zuschlag ? estimate.zuschlag.eur : null,
          spezialZuschlagGrund: estimate.zuschlag ? estimate.zuschlag.grund : null,
          ...(formData.is_festpreis && formData.festpreis_eur ? {
            festpreisEur: parseFloat(formData.festpreis_eur),
            festpreisLabel: `Festpreis ${parseFloat(formData.festpreis_eur).toFixed(2)} €`,
            estimatedEarningEur: parseFloat(formData.festpreis_eur),
          } : {
            festpreisEur: null,
            festpreisLabel: null,
            // Tarif-Schätzung inkl. Anfahrt persistieren (für Anzeige + Fahrer-App).
            ...(estimate.totalFareEur !== null ? { estimatedEarningEur: estimate.totalFareEur } : {}),
          }),
          // Aktualisiere weitere Felder nur, wenn sie sich geändert haben
          ...(currentRide.customerId !== formData.customer_id ? { customerId: formData.customer_id } : {}),
          ...(currentRide.customerName !== formData.customer_name ? { customerName: formData.customer_name } : {}),
          ...(currentRide.customerPhone !== formData.customer_phone ? { customerPhone: formData.customer_phone } : {}),
          // Fahrerzuweisung wird nicht im Bearbeitungsmodus geändert — dafür gibt es den Zuweisungsmodus
          // Füge geschätzte Fahrtdauer hinzu, wenn berechnet
          ...(estimatedTimeMinutes !== null && estimatedTimeMinutes !== undefined ? { estimatedTimeMinutes } : {}),
          ...(distanceKm !== null && distanceKm !== undefined ? { distanceKm: parseFloat(distanceKm.toFixed(2)) } : {})
        };
        
        // Für Vorbestellungen, Zeitpunkt aktualisieren
        if (currentRide.orderType === 1 && formData.scheduled_time) {
          updateData.scheduledTime = formData.scheduled_time.toISOString();
        }
        
        dispatch(updateRide(updateData)).then((result) => {
          if (!result.error) {
            onSuccess?.(result.payload);
            onClose?.();
          }
        });
      } else {
        // Prüfen, ob ein neuer Kunde erstellt werden muss
        let customerId = formData.customer_id;
        let customerName = formData.customer_name;

        // Laufkunde-Fallback: Disponent hat weder einen Kunden gewählt
        // noch einen Namen für einen neuen Telefonkunden eingegeben.
        const isLaufkunde = !createNewCustomer && !customerId;

        if (createNewCustomer) {
          try {
            // Erstelle neuen Telefonkunden (konsistent mit dem Telefonkunden-Konzept)
            const newCustomerData = {
              isPhoneCustomer: true, // Markiert als Telefonkunde
              name: formData.customer_name,
              phoneNumber: formData.customer_phone || '',
              // Keine Adresse hier, da wir diese im Fahrtformular nicht abfragen
              // Das ist konsistent mit dem vereinfachten Telefonkunden-Formular
            };

            const resultAction = await dispatch(createCustomer(newCustomerData));

            if (createCustomer.fulfilled.match(resultAction)) {
              customerId = resultAction.payload.id;
              customerName = resultAction.payload.user.name;
            } else {
              // Wenn der Kunde nicht erstellt werden konnte, Fehler anzeigen
              console.error('Fehler beim Erstellen des Kunden:', resultAction.error);
              return;
            }
          } catch (error) {
            console.error('Fehler beim Erstellen des Kunden:', error);
            return;
          }
        } else if (isLaufkunde) {
          customerId = null;
          customerName = DEFAULT_PHONE_CUSTOMER_NAME;
        }
        
        // Getippte Adressen ohne Vorschlagsauswahl zuerst nachgeocodieren, damit
        // Schätzung und angelegte Fahrt dieselben Koordinaten tragen.
        setIsCalculatingRoute(true);
        let estimate;
        let locations;
        try {
          const resolvedCoordinates = await resolveFormCoordinates();
          if (!resolvedCoordinates.ok) {
            setFormErrors(prev => ({ ...prev, [resolvedCoordinates.field]: resolvedCoordinates.message }));
            return;
          }
          locations = resolvedCoordinates.locations;
          estimate = await resolveEstimate(locations);
        } finally {
          setIsCalculatingRoute(false);
        }
        const estimatedTimeMinutes = estimate.durationInMinutes;
        const distanceKm = estimate.distanceKm;
        const anfahrt = estimate.anfahrt;

        // Kundentyp bestimmen: Telefonkunde oder App-Kunde
        const isPhoneCustomer = (createNewCustomer || isLaufkunde)
          ? true  // Neue Kunden im Portal und Laufkunden sind immer Telefonkunden
          : (customers.find(c => c.id === customerId)?.user?.isPhoneCustomer || false);

        // Schnellanlage: Platzhalter-Verordnung zuerst anlegen, damit die
        // Fahrt (bzw. alle Dauerauftrags-Einzelfahrten) sofort eine gültige
        // medicalTransportOrderId trägt (§8.1). Patient = Fahrt-Kunde.
        let medicalOrderIdForSave = formData.is_krankenfahrt ? (formData.medical_transport_order_id || null) : null;
        let medicalOrderForSnapshot = selectedMedicalTransportOrder;
        if (formData.is_krankenfahrt && createNewMedicalOrder && !medicalOrderIdForSave) {
          try {
            const placeholderOrder = await createPlaceholderMedicalOrder(customerName, customerId);
            medicalOrderIdForSave = placeholderOrder.id;
            medicalOrderForSnapshot = placeholderOrder;
            setCreateNewMedicalOrder(false);
          } catch (placeholderError) {
            setFormErrors(prev => ({ ...prev, medical_transport_order_id: placeholderError.message }));
            return;
          }
        }

        const medicalTransportSnapshot = formData.is_krankenfahrt
          ? buildMedicalTransportSnapshot(medicalOrderForSnapshot)
          : null;

        // Rechnungskunden-Schnellanlage: neuen (unvollständigen) Firmenkunden
        // zuerst anlegen, damit die Fahrt(en) eine gültige invoiceCustomerId
        // tragen (§8.4).
        let invoiceCustomerIdForSave = (formData.is_rechnungsfahrt && formData.invoice_customer_id) ? formData.invoice_customer_id : null;
        let invoiceCustomerNameForSave = invoiceCustomerIdForSave
          ? (invoiceCustomers.find((c) => c.id === invoiceCustomerIdForSave)?.displayName
             || invoiceCustomers.find((c) => c.id === invoiceCustomerIdForSave)?.companyName || null)
          : null;
        if (formData.is_rechnungsfahrt && !invoiceCustomerIdForSave && newInvoiceCustomerName.trim()) {
          try {
            const quickCustomer = await createQuickInvoiceCustomer();
            invoiceCustomerIdForSave = quickCustomer.id;
            invoiceCustomerNameForSave = quickCustomer.name;
            setNewInvoiceCustomerName('');
          } catch (quickError) {
            setFormErrors(prev => ({ ...prev, invoice_customer_id: quickError.message || 'Rechnungskunde konnte nicht angelegt werden.' }));
            return;
          }
        }

        // Bestimme Status basierend auf Bestelltyp und ob ein Fahrer ausgewählt wurde
        let status;
        if (formData.driver_id) {
          // Wenn ein Fahrer ausgewählt wurde, setze Status entsprechend dem Bestelltyp
          status = formData.order_type === '1' ? 'pre_accepted' : 'dispatcher_pending';
        } else {
          // Wenn kein Fahrer ausgewählt wurde, verwende den Standard-Status
          status = formData.order_type === '1' ? 'scheduled' : 'open';
        }
        
        // Erstelle Daten für die Fahrt basierend auf dem Bestelltyp
        const commonRideData = {
          customerId: customerId || null,
          customerName: customerName || DEFAULT_PHONE_CUSTOMER_NAME,
          customerPhone: formData.customer_phone || null, // Telefonnummer des Kunden hinzufügen
          start: formData.pickup_address,
          startLatitude: locations.pickup?.latitude || 0,
          startLongitude: locations.pickup?.longitude || 0,
          destination: formData.destination_address,
          destinationLatitude: locations.destination?.latitude || 0,
          destinationLongitude: locations.destination?.longitude || 0,
          // Zwischenstopps (§2.1). Überschreibt destination* mit dem letzten
          // Halt; ohne Halte stehen dort null-Werte und die Felder oben
          // bleiben unverändert.
          ...buildStopFields({
            intermediateStops: (locations.stops || []).map((s) => ({
              address: s.address,
              latitude: s.location?.latitude,
              longitude: s.location?.longitude,
            })),
            destinationAddress: formData.destination_address,
            destinationLocation: locations.destination,
            legs: estimate.legs,
          }),
          vehicleType: parseInt(formData.vehicle_type, 10),
          specialCapabilities: formData.special_capabilities || [],
          orderType: parseInt(formData.order_type, 10),
          status: status,
          notes: buildRideNotes(anfahrt?.note),
          // Anfahrtskosten (außerhalb→außerhalb); bei Dauerauftrag über
          // generateRecurringRides an alle Einzelfahrten vererbt (gleiche Route).
          ...(anfahrt ? { anfahrtEur: anfahrt.eur, anfahrtKm: anfahrt.km } : {}),
          // Spezialzuschlag (Rollstuhl/Tragestuhl/Liegend), bei Anlage fixiert
          // und in estimatedEarningEur enthalten — spezialzuschlaege_konzept.md §3.2.
          ...(estimate.zuschlag
            ? { spezialZuschlagEur: estimate.zuschlag.eur, spezialZuschlagGrund: estimate.zuschlag.grund }
            : {}),
          isKrankenfahrt: formData.is_krankenfahrt,
          medicalTransportOrderId: medicalOrderIdForSave,
          medicalTransportSnapshot,
          medicalTransportBillingStatus: formData.is_krankenfahrt ? 'not_ready' : null,
          medicalTransportProofStatus: formData.is_krankenfahrt ? 'missing' : null,
          // Bewusste Übersteuerung eines harten Verordnungs-Hinweises (oder null).
          medicalTransportWarningOverride: buildMedicalWarningOverride(),
          isRechnungsfahrt: formData.is_rechnungsfahrt,
          invoiceCustomerId: invoiceCustomerIdForSave,
          invoiceCustomerName: invoiceCustomerNameForSave,
          invoiceBillingStatus: invoiceCustomerIdForSave ? 'pending' : null,
          // KEIN paymentMethod: 'invoice' hier — Zahlungsfelder sind per Rules
          // clientseitig gesperrt (PayPal-Konzept 12.2); die Cloud Function
          // onInvoiceRideCompleted setzt den Snapshot beim Fahrtabschluss.
          isPhoneCustomer: isPhoneCustomer,
          ...(draftState.incomingCallDraft?.incomingCallSessionId ? {
            incomingCallSessionId: draftState.incomingCallDraft.incomingCallSessionId,
            bookingSource: 'incoming_call'
          } : {}),
          ...(formData.is_festpreis && formData.festpreis_eur ? {
            isFestpreis: true,
            festpreisEur: parseFloat(formData.festpreis_eur),
            festpreisLabel: `Festpreis ${parseFloat(formData.festpreis_eur).toFixed(2)} €`,
            estimatedEarningEur: parseFloat(formData.festpreis_eur),
          } : {
            // Tarif-Schätzung inkl. Anfahrt persistieren (Anzeige + Fahrer-App).
            ...(estimate.totalFareEur !== null ? { estimatedEarningEur: estimate.totalFareEur } : {}),
          }),
          // Füge geschätzte Fahrtdauer hinzu, wenn berechnet
          ...(estimatedTimeMinutes !== null && estimatedTimeMinutes !== undefined ? { estimatedTimeMinutes } : {}),
          ...(distanceKm !== null && distanceKm !== undefined ? { distanceKm: parseFloat(distanceKm.toFixed(2)) } : {})
        };

        // Spezifische Daten je nach Bestelltyp
        let specificRideData = {};
        
        if (formData.order_type === '1') { // Vorbestellung
          // Hier stellen wir sicher, dass das Datum korrekt formatiert wird
          // und als ISO-String für die API gespeichert wird
          const scheduledTime = formData.scheduled_time;
          
          specificRideData = {
            scheduledTime: scheduledTime.toISOString(),
          };
        } else if (formData.order_type === '3') { // Dauerauftrag
          // Für Daueraufträge generieren wir alle Einzelfahrten
          const recurringRides = generateRecurringRides(commonRideData);
          
          // Erstelle alle Fahrten in Serie
          try {
            let createdRidesCount = 0;
            const errors = [];
            
            for (const rideData of recurringRides) {
              try {
                const result = await dispatch(createRide(rideData));
                if (!result.error) {
                  createdRidesCount++;
                } else {
                  errors.push(`Fehler bei Fahrt am ${new Date(rideData.scheduledTime).toLocaleDateString('de-DE')}: ${result.error}`);
                }
              } catch (error) {
                errors.push(`Fehler bei Fahrt am ${new Date(rideData.scheduledTime).toLocaleDateString('de-DE')}: ${error.message}`);
              }
            }
            
            if (errors.length === 0) {
              // Alle Fahrten erfolgreich erstellt
              alert(`Dauerauftrag erfolgreich erstellt! ${createdRidesCount} Fahrten wurden geplant.`);
              onClose?.();
              return; // Beende die Funktion hier für Daueraufträge
            } else {
              // Teilweise Fehler
              alert(`Dauerauftrag teilweise erstellt. ${createdRidesCount} von ${recurringRides.length} Fahrten wurden erfolgreich erstellt.\n\nFehler:\n${errors.join('\n')}`);
              onClose?.();
              return; // Beende die Funktion hier für Daueraufträge
            }
          } catch (error) {
            alert(`Fehler beim Erstellen des Dauerauftrags: ${error.message}`);
            return; // Beende die Funktion bei Fehlern
          }
        }
        
        // Füge Fahrer-Daten hinzu, wenn ein Fahrer ausgewählt wurde
        if (formData.driver_id) {
          // Finde den ausgewählten Fahrer aus den verfügbaren Fahrern
          const selectedDriver = (formData.order_type === '1' || formData.order_type === '3')
            ? allDrivers.find(d => d.id === formData.driver_id)
            : availableDrivers.find(d => d.id === formData.driver_id);
          
          if (selectedDriver) {
            specificRideData = {
              ...specificRideData,
              driverId: selectedDriver.id,
              driverName: selectedDriver.name || selectedDriver.user?.name || 'Unbekannt',
              driverProfilePic: selectedDriver.profilePic || selectedDriver.user?.profile_picture || '',
              driverRating: selectedDriver.rating || null,
              driverRatingCount: selectedDriver.ratingCount || null,
              assignedByDispatcher: true, // Cloud Function: Push an Fahrer senden
            };
            
            // Füge Zeitstempelfeld für Fahrerannahme hinzu (bei Vor- und Dauerbestellungen)
            // Bei Sofortbestellungen wird der Timestamp erst gesetzt wenn der Fahrer annimmt
            if (formData.order_type === '1' || formData.order_type === '3') {
              specificRideData.acceptedAt = new Date().toISOString();
            } else {
              specificRideData.is_active = false;
            }
            
            // Fahrzeugdaten nur bei Sofortbestellungen hinzufügen
            // Bei Vor-/Dauerbestellungen werden die Fahrzeugdaten bei Fahrtantritt durch die Mobile-App gesetzt
            if (formData.order_type !== '1' && formData.order_type !== '3') {
              if (selectedDriver.vehicle) {
                specificRideData.driverVehicleId = selectedDriver.vehicle.id || null;
                specificRideData.driverModel = selectedDriver.vehicle.model || null;
                specificRideData.driverLicensePlate = selectedDriver.vehicle.licensePlate || null;
              } else if (formData.vehicle_id) {
                // Fallback: Fahrzeugdaten aus der Fahrzeug-Auswahl laden
                const selectedVehicle = availableVehicles.find(v => v.id === formData.vehicle_id);
                if (selectedVehicle) {
                  specificRideData.driverVehicleId = selectedVehicle.id;
                  specificRideData.driverModel = selectedVehicle.model || null;
                  specificRideData.driverLicensePlate = selectedVehicle.licensePlate || null;
                }
              }
            }
          }
        }
        
        // Kombiniere die Daten und erstelle die Fahrt
        const rideData = {
          ...commonRideData,
          ...specificRideData
        };
        
        dispatch(createRide(rideData)).then((result) => {
          if (!result.error) {
            // Wenn "Als Vorlage speichern" ausgewählt ist, Fahrtdetails als Vorlage speichern
            if (saveAsTemplate) {
              dispatch(saveRideTemplate({
                customer_name: formData.customer_name,
                customer_phone: formData.customer_phone,
                customer_id: customerId,
                pickup_address: formData.pickup_address,
                pickup_location: formData.pickup_location,
                destination_address: formData.destination_address,
                destination_location: formData.destination_location,
                vehicle_type: formData.vehicle_type,
                order_type: formData.order_type
              }));
            }
            onSuccess?.(result.payload);
            onClose?.();
          }
        });
      }
    }
  };

  const handleCancel = () => {
    onClose?.();
  };
  
  const isLoading = rideLoading || driversLoading || vehiclesLoading || customersLoading;

  // Feldkennungen wechseln bei jedem Öffnen: Sonst findet der Browser seine
  // gemerkten Eingaben aus der letzten Fahrt wieder (docs 3.8).
  const fieldIdRef = useRef(null);
  if (!fieldIdRef.current) fieldIdRef.current = createFieldIdFactory();
  const fieldId = fieldIdRef.current;

  // Tastaturführung – siehe docs/TASTATURGEFUEHRTE_FAHRTERFASSUNG.md, Abschnitt 3.
  const [helpOpen, setHelpOpen] = useState(false);
  const helpReturnRef = useRef(null);

  const openHelp = () => {
    const active = document.activeElement;
    helpReturnRef.current =
      active && typeof active.setSelectionRange === 'function'
        ? { element: active, start: active.selectionStart, end: active.selectionEnd }
        : { element: active, start: null, end: null };
    setHelpOpen(true);
  };

  const closeHelp = () => {
    setHelpOpen(false);
    const saved = helpReturnRef.current;
    helpReturnRef.current = null;
    if (!saved?.element?.isConnected) return;
    setTimeout(() => {
      saved.element.focus();
      if (saved.start !== null) {
        try {
          saved.element.setSelectionRange(saved.start, saved.end);
        } catch (e) {
          // Manche Feldtypen erlauben setSelectionRange nicht – Fokus genügt.
        }
      }
    }, 0);
  };

  const { containerProps, focusField } = useFormKeyboardNav({
    enterAdvances: globalSettings?.keyboardEnterAdvances ?? true,
    onSubmit: handleSubmit,
    canSubmit: !isLoading && !isCalculatingRoute,
    onSubmitBlocked: () => focusField('pickupAddress'),
    onEscape: () => handleCancel(),
    onHelp: openHelp,
    altShortcuts: isAssignMode
      ? {}
      : {
          // Bestelltyp: 2 = Sofort, 1 = Vorbestellung, 3 = Dauerauftrag
          s: () => handleOrderTypeChange({ target: { value: '2' } }),
          v: () => handleOrderTypeChange({ target: { value: '1' } }),
          d: () => handleOrderTypeChange({ target: { value: '3' } }),
        },
  });

  // Die Buttonleiste liegt mobil fixiert über dem Dialog und damit ausserhalb
  // des Textflusses. Ohne Ausgleich verdeckt sie das Ende des Formulars – im
  // Zuweisungsmodus genau die Fahrerauswahl. Ihre Hoehe haengt von der Anzahl
  // der Buttons ab (Modus) und von Umbruechen, deshalb wird sie gemessen statt
  // geraten und weiter unten als Platzhalter reserviert.
  useEffect(() => {
    const el = actionBarRef.current;
    if (!el || !isMobile) {
      setActionBarHeight(0);
      return undefined;
    }
    const update = () => setActionBarHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile, isEditMode, isAssignMode]);

  // Info-Symbol im Dialog-Header öffnet dieselbe Hilfe wie F1.
  if (helpRef) {
    helpRef.current = openHelp;
  }

  // Beim Anlegen den Cursor ins erste Feld setzen. `autoFocus` allein genügt
  // nicht: Der Fokus-Trap des Dialogs greift nach dem Mount und zieht den Fokus
  // auf den Dialog-Rahmen. Deshalb erst danach setzen.
  useEffect(() => {
    // Beim Bearbeiten gibt es kein naheliegendes erstes Feld – dort bleibt der
    // Fokus beim Dialog.
    const target = isAssignMode ? 'driver' : isEditMode ? null : 'customer';
    if (!target) return undefined;
    const timer = setTimeout(() => focusField(target), 0);
    return () => clearTimeout(timer);
  }, [isEditMode, isAssignMode, focusField]);

  
  if (isLoading && (isEditMode || isAssignMode)) {
    return <Loading message="Daten werden geladen..." />;
  }
  
  if (rideError && (isEditMode || isAssignMode)) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Daten"
        message={rideError}
        onRetry={() => dispatch(fetchRideById(rideId))}
      />
    );
  }
  
  if ((isEditMode || isAssignMode) && currentRide?.status === 'cancelled') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Diese Fahrt wurde storniert und kann nicht mehr bearbeitet oder zugewiesen werden.
        </Alert>
        <Button
          variant="contained"
          onClick={onClose}
        >
          Zurück zur Fahrtansicht
        </Button>
      </Box>
    );
  }

  if (isAssignMode && !['scheduled', 'open', 'dispatcher_pending', 'pre_accepted', 'accepted'].includes(currentRide?.status)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Diese Fahrt kann nicht mehr zugewiesen werden (Status: {currentRide?.status}).
        </Alert>
        <Button
          variant="contained"
          onClick={onClose}
        >
          Zurück zur Fahrtansicht
        </Button>
      </Box>
    );
  }
  
  // Customer-Options erstellen (inkl. "Neuer Kunde" Option)
  const customerOptions = customers;

  // "Nur als Vorlage anlegen" nur mit echtem Kunden + Abholung + Ziel + Fahrzeugtyp.
  const canSaveTemplate = !!(formData.customer_id && formData.pickup_address && formData.destination_address && formData.vehicle_type);

  // Das Vorlagen-Feld ist immer sichtbar, auch wenn es (noch) nichts zu wählen
  // gibt: Sonst erscheint es mitten in der Erfassung und schiebt die Felder
  // darunter weg – bei geöffneter Adress-Vorschlagsliste samt Dropdown
  // (docs/TASTATURGEFUEHRTE_FAHRTERFASSUNG.md 3.6). Der Hinweistext sagt,
  // woran es liegt, statt das Feld wortlos verschwinden zu lassen.
  const templatesSelectable = !!formData.customer_id && customerTemplates.length > 0;
  const templateHint = !formData.customer_id
    ? 'Erst einen Kunden auswählen'
    : customerTemplates.length === 0
      ? 'Für diesen Kunden sind keine Vorlagen gespeichert'
      : `${customerTemplates.length} ${customerTemplates.length === 1 ? 'Vorlage' : 'Vorlagen'} für diesen Kunden`;



  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <GlobalStyle />
      <Box sx={{ flexGrow: 1, overflow: 'visible' }}>
      {/* Wartezeit-Anzeige – nur beim Erstellen neuer Fahrten; Optik analog zum Dashboard */}
      {!isEditMode && !isAssignMode && waitTimes && (() => {
        const waitTimeItems = [
          { key: 'general', label: 'PKW', value: waitTimes.general, icon: <CarIcon /> },
        ];
        if (existingVehicleTypes.has(2)) {
          waitTimeItems.push({ key: 'grossraum', label: 'Großraum', value: waitTimes.grossraum, icon: <KombiIcon /> });
        }
        if (existingVehicleTypes.has(3)) {
          waitTimeItems.push({ key: 'kleinbus7', label: 'Kleinbus 7', value: waitTimes.kleinbus7, icon: <MinibusIcon /> });
        }
        if (existingVehicleTypes.has(4)) {
          waitTimeItems.push({ key: 'kleinbus8', label: 'Kleinbus 8', value: waitTimes.kleinbus8, icon: <MinibusIcon /> });
        }

        return (
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 1.5,
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))',
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 190 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 34,
                    height: 34,
                    borderRadius: 1,
                    flexShrink: 0,
                    bgcolor: '#1976d218',
                    '& .MuiSvgIcon-root': { fontSize: 20, color: '#1976d2' },
                  }}
                >
                  <TimerIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    Aktuelle Wartezeiten
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Nach Fahrzeugtyp
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
                {waitTimeItems.map((item) => {
                  const severity = getWaitTimeSeverity(item.value);
                  const softBg = severity === 'error'
                    ? 'rgba(211, 47, 47, 0.08)'
                    : severity === 'warning'
                      ? 'rgba(237, 108, 2, 0.08)'
                      : 'rgba(46, 125, 50, 0.08)';

                  return (
                    <Chip
                      key={item.key}
                      icon={item.icon}
                      label={`${item.label} ${item.value || '–'}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 30,
                        borderColor: `${severity}.main`,
                        color: `${severity}.dark`,
                        bgcolor: softBg,
                        fontWeight: 700,
                        '& .MuiChip-icon': { color: `${severity}.dark` },
                      }}
                    />
                  );
                })}

                {specialCapabilityItems.length > 0 && (
                  <MedicalIcon
                    fontSize="small"
                    sx={{ color: 'error.main', ml: 0.5 }}
                    titleAccess="Krankenfahrt-Spezialfahrzeuge"
                  />
                )}
                {specialCapabilityItems.map((item) => {
                  const severity = item.isAvailable ? 'success' : 'error';
                  const softBg = item.isAvailable
                    ? 'rgba(46, 125, 50, 0.08)'
                    : 'rgba(211, 47, 47, 0.08)';
                  return (
                    <Chip
                      key={item.key}
                      icon={item.icon}
                      label={`${item.label} ${item.isAvailable ? 'Verfügbar' : 'Nicht verfügbar'}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 30,
                        borderColor: `${severity}.main`,
                        color: `${severity}.dark`,
                        bgcolor: softBg,
                        fontWeight: 700,
                        '& .MuiChip-icon': { color: `${severity}.dark` },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Paper>
        );
      })()}

      {/*
        Kein implizites Absenden: Enter darf die Fahrt nie anlegen, nur der
        Button oder Strg+Enter. Der Submit-Button ist deshalb type="button",
        und der submit-Event wird abgefangen, falls ihn doch etwas auslöst.
        Siehe docs/TASTATURGEFUEHRTE_FAHRTERFASSUNG.md 3.4c.
      */}
      <form onSubmit={(e) => e.preventDefault()} autoComplete="off" {...containerProps}>
        {isAssignMode ? (
          // Assignment Form - Fahrtdaten grau/deaktiviert + Fahrerzuweisung aktiv
          <>
            {/* Hinweis bei Neuzuweisung */}
            {isAssignMode && currentRide?.driverId && (
              <Alert
                severity={currentRide.status === 'accepted' ? 'warning' : 'info'}
                sx={{ mb: 2 }}
              >
                {currentRide.status === 'accepted'
                  ? `Achtung: Fahrer ${currentRide.driverName || 'Unbekannt'} hat diese Fahrt bereits angenommen. Bei einer Neuzuweisung wird ihm die Fahrt entzogen und er wird benachrichtigt.`
                  : `Diese Fahrt ist derzeit Fahrer ${currentRide.driverName || 'Unbekannt'} zugewiesen. Bei einer Neuzuweisung wird ihm die Fahrt entzogen und er wird benachrichtigt.`}
              </Alert>
            )}

            {/* Fahrtdaten (deaktiviert) */}
            <FormPaper elevation={2} sx={{ bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom color="text.secondary">
                Fahrtdaten
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Kunde"
                    value={currentRide?.customerName || 'Unbekannt'}
                    disabled
                    InputProps={{
                      startAdornment: <PersonIcon color="disabled" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={currentRide?.customerPhone || currentRide?.customer?.phone || 'Nicht angegeben'}
                    disabled
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Abholadresse"
                    value={currentRide?.start || ''}
                    disabled
                    InputProps={{
                      startAdornment: <LocationIcon color="disabled" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Zieladresse"
                    value={currentRide?.destination || ''}
                    disabled
                    InputProps={{
                      startAdornment: <LocationIcon color="disabled" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Bestelltyp"
                    value={currentRide?.orderType === 1 ? 'Vorbestellung' : currentRide?.orderType === 3 ? 'Dauerauftrag' : 'Sofortbestellung'}
                    disabled
                    InputProps={{
                      startAdornment: currentRide?.orderType === 1
                        ? <ScheduleIcon color="disabled" sx={{ mr: 1 }} />
                        : <ImmediateIcon color="disabled" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Fahrzeugtyp"
                    value={getVehicleTypeDescription(currentRide?.vehicleType)}
                    disabled
                  />
                </Grid>
                {currentRide?.scheduledTime && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Geplante Zeit"
                      value={new Date(currentRide.scheduledTime).toLocaleString('de-DE', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      disabled
                      InputProps={{
                        startAdornment: <ScheduleIcon color="disabled" sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </FormPaper>

            {/* Fahrer zuweisen (aktiv) */}
            <FormPaper elevation={2}>
              <Typography variant="h6" gutterBottom>
                Fahrer zuweisen
              </Typography>

              {currentRide?.orderType === 1 && (formData.driver_id || currentRide?.driverId || currentRide?.status === 'pre_accepted') && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Bei zugewiesenen Vorbestellungen prüft NextCab ab dem berechneten Freigabezeitpunkt,
                  ob der Fahrer online ist. Bei externen Abholungen kann diese Prüfung entsprechend der
                  Google-Maps-Fahrzeit früher erfolgen. Ist der Fahrer offline, wird die Fahrt wieder zur
                  Disposition freigegeben.
                </Alert>
              )}

              {currentRide?.driverId && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Diese Fahrt ist bereits <strong>{currentRide.driverName || 'einem Fahrer'}</strong> zugewiesen
                  {currentRide.status === 'dispatcher_pending' && ' (wartet auf Annahme)'}.
                  Bei Neuzuweisung wird der bisherige Fahrer automatisch entfernt und benachrichtigt.
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    id={fieldId('driver_id')}
                    options={(currentRide?.orderType === 1 || currentRide?.orderType === 3 || currentRide?.convertedToOpenRide) ? allDrivers : availableDrivers}
                    getOptionLabel={getDriverOptionLabel}
                    value={((currentRide?.orderType === 1 || currentRide?.orderType === 3 || currentRide?.convertedToOpenRide) ? allDrivers : availableDrivers).find(d => d.id === formData.driver_id) || null}
                    onChange={handleDriverChange}
                    getOptionDisabled={isDriverOptionDisabled}
                    renderInput={(params) =>
                      <TextField
                        {...params}
                        inputProps={{ ...params.inputProps, autoComplete: NO_AUTOFILL }}
                        data-nav="driver"
                        label="Fahrer"
                        fullWidth
                        required
                        error={!!formErrors.driver_id}
                        helperText={formErrors.driver_id}
                      />
                    }
                    renderOption={(props, option) => renderDriverOption(props, option, {
                      requireOnlineWithVehicle: !(currentRide?.orderType === 1 || currentRide?.orderType === 3 || currentRide?.convertedToOpenRide),
                      mismatchReason: getDriverMismatchReason(option),
                      blocking: isDriverOptionDisabled(option)
                    })}
                  />
                  {selectedDriverMismatch && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Dieser Fahrer erfüllt die Anforderung der Fahrt nicht: {selectedDriverMismatch}.
                    </Alert>
                  )}
                  <FormHelperText>
                    {((currentRide?.orderType === 1 || currentRide?.orderType === 3 || currentRide?.convertedToOpenRide) ? allDrivers : availableDrivers).length === 0
                      ? 'Keine verfügbaren Fahrer gefunden.'
                      : (currentRide?.orderType === 1 || currentRide?.orderType === 3 || currentRide?.convertedToOpenRide)
                        ? 'Für Vor-/Dauerbestellungen können alle Fahrer zugewiesen werden. Fahrzeugdaten werden bei Fahrtantritt gesetzt.'
                        : 'Für Sofortbestellungen können nur Online-Fahrer mit zugewiesenem Fahrzeug zugewiesen werden.'}
                  </FormHelperText>
                </Grid>

                {/* Fahrzeug: Bei Sofortbestellungen auto-filled & required, bei Vor-/Dauerbestellungen optional */}
                {formData.driver_id && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    {(currentRide?.orderType === 1 || currentRide?.orderType === 3) ? (
                      // Vorbestellung/Dauerauftrag: optionales Fahrzeug-Dropdown
                      <>
                        <Autocomplete
                          id="vehicle_id"
                          options={availableVehicles}
                          getOptionLabel={(option) => `${option.model || 'Unbekannt'} (${option.licensePlate || 'Kein Kennzeichen'})`}
                          value={availableVehicles.find(v => v.id === formData.vehicle_id) || null}
                          onChange={handleVehicleChange}
                          renderInput={(params) =>
                            <TextField
                              {...params}
                              inputProps={{ ...params.inputProps, autoComplete: NO_AUTOFILL }}
                              label="Fahrzeug (optional)"
                              fullWidth
                              error={!!formErrors.vehicle_id}
                              helperText={formErrors.vehicle_id || 'Fahrzeugdaten werden bei Fahrtantritt durch die Mobile-App gesetzt.'}
                            />
                          }
                        />
                      </>
                    ) : (
                      // Sofortbestellung: Fahrzeug automatisch vom Fahrer übernommen
                      (() => {
                        const selectedDriver = drivers.find(d => d.id === formData.driver_id);
                        const vehicleInfo = selectedDriver?.vehicle || selectedDriver?.car;
                        return (
                          <TextField
                            fullWidth
                            label="Fahrzeug"
                            value={vehicleInfo ? `${vehicleInfo.model || 'Unbekannt'} (${vehicleInfo.licensePlate || 'Kein Kennzeichen'})` : 'Kein Fahrzeug zugewiesen'}
                            disabled
                            error={!vehicleInfo}
                            helperText={vehicleInfo ? 'Automatisch vom Fahrer übernommen' : 'Diesem Fahrer ist kein Fahrzeug zugewiesen.'}
                            InputProps={{
                              startAdornment: <CarIcon color={vehicleInfo ? "action" : "error"} sx={{ mr: 1 }} />
                            }}
                          />
                        );
                      })()
                    )}
                  </Grid>
                )}
              </Grid>
            </FormPaper>
          </>
        ) : (
          // Create/Edit Form
          <>
            {draftState.duplicateRide && !isEditMode && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {['completed', 'cancelled', 'cancelled_by_driver'].includes(draftState.duplicateRide.status)
                  ? 'Fahrt wird aus einer vorherigen Fahrt erstellt. Bitte überprüfen Sie die vorausgefüllten Daten.'
                  : `${wording.pick('Zusätzliches Taxi', 'Zusätzlicher Wagen')} zu einer laufenden Fahrt wird angelegt (Sofortbestellung). Bitte überprüfen Sie die vorausgefüllten Daten und weisen Sie anschließend einen Fahrer zu.`}
              </Alert>
            )}
            <FormPaper elevation={2}>
              <Typography variant="h6" gutterBottom>
                Kundeninformation
              </Typography>
              
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Autocomplete
                    id={fieldId('customer-select')}
                    options={customerOptions}
                    sx={{ width: '100%' }}
                    getOptionLabel={(option) => {
                      // Wenn ein String übergeben wird (direktes Tippen)
                      if (typeof option === 'string') {
                        return option;
                      }
                      // Wenn eine "Neuen Kunden erstellen" Option ist
                      if (option.inputValue) {
                        return option.inputValue;
                      }
                      // Normale Kundenoption
                      return option.name || option.user?.name || '';
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    freeSolo
                    value={createNewCustomer ? { inputValue: formData.customer_name } : customerOptions.find(c => c.id === formData.customer_id) || null}
                    onChange={handleCustomerChange}
                    filterOptions={(options, params) => {
                      const { inputValue } = params;

                      // Filter by name or phone number
                      const filtered = inputValue
                        ? options.filter((option) => {
                            const name = (option.name || option.user?.name || '').toLowerCase();
                            const phone = (option.phoneNumber || option.user?.phoneNumber || '').toLowerCase();
                            const term = inputValue.toLowerCase();
                            return name.includes(term) || phone.includes(term);
                          })
                        : options;

                      // Suggest the creation of a new value
                      const isExisting = options.some((option) => inputValue === option.name || inputValue === option.user?.name);

                      if (inputValue !== '' && !isExisting) {
                        filtered.push({
                          inputValue,
                          name: `"${inputValue}" als neuen Telefonkunden anlegen`
                        });
                      }

                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{ ...params.inputProps, autoComplete: NO_AUTOFILL }}
                        data-nav="customer"
                        // Beim Anlegen steht der Cursor sofort im ersten Feld –
                        // der Disponent hat den Kunden am Telefon. Beim
                        // Bearbeiten und Zuweisen waere das falsch: dort ist der
                        // Kunde schon gesetzt.
                        autoFocus={!isEditMode && !isAssignMode}
                        label="Kunde (Name oder Telefonnummer)"
                        placeholder="Leer lassen für Laufkunde (Telefonkunde ohne Profil)"
                        error={!!formErrors.customer_id}
                        helperText={formErrors.customer_id}
                        sx={{ minWidth: { xs: 0, sm: '350px' }, width: '100%' }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <PersonIcon color="primary" sx={{ mr: 1 }} />
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      // Wenn es die "Neuen Kunden erstellen" Option ist
                      if (option.inputValue) {
                        return (
                          <li {...props}>
                            <Typography variant="body1" color="primary">
                              {option.name} (als Telefonkunde)
                            </Typography>
                          </li>
                        );
                      }
                      
                      // Normale Kundenoption
                      return (
                        <li {...props}>
                          <Box>
                            <Typography variant="body1">{option.name || option.user?.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {option.email || option.user?.email} | {option.phone || option.user?.phoneNumber}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                  />
                </Grid>
                
                {!createNewCustomer && (
                  <Grid size={{ xs: 12, md: 2 }} sx={{ 
                    display: { xs: 'none', md: 'flex' }, 
                    justifyContent: 'center', 
                    alignItems: 'center',
                  }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: 'text.secondary',
                      }}>
                      ODER
                    </Typography>
                  </Grid>
                )}
                
                {!createNewCustomer && (
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Autocomplete
                        id={fieldId('template-select')}
                        disabled={!templatesSelectable}
                        options={customerTemplates ? [...customerTemplates].sort((a, b) => {
                        // Sortierung priorisiert zuerst nach Kundennamen (falls vorhanden)
                        if (a.customer_name && b.customer_name) {
                          return a.customer_name.localeCompare(b.customer_name);
                        } else if (a.customer_name) {
                          return -1; // Vorlagen mit Kundennamen zuerst
                        } else if (b.customer_name) {
                          return 1;
                        }
                        // Dann nach Startadresse
                        return a.pickup_address.localeCompare(b.pickup_address);
                      }) : []}
                      getOptionLabel={(option) => {
                        if (option.customer_name) {
                          return `${option.customer_name}: ${option.pickup_address} → ${option.destination_address}`;
                        }
                        return `${option.pickup_address} → ${option.destination_address}`;
                      }}
                      filterOptions={(options, state) => {
                        // Erweiterte Filterung, um sowohl nach Kundennamen als auch nach Adressen zu filtern
                        const { inputValue } = state;
                        return options.filter(option => {
                          const customerMatch = option.customer_name && 
                            option.customer_name.toLowerCase().includes(inputValue.toLowerCase());
                          const pickupMatch = option.pickup_address.toLowerCase().includes(inputValue.toLowerCase());
                          const destMatch = option.destination_address.toLowerCase().includes(inputValue.toLowerCase());
                          return customerMatch || pickupMatch || destMatch;
                        });
                      }}
                      onChange={handleTemplateSelect}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{ ...params.inputProps, autoComplete: NO_AUTOFILL }}
                          data-nav="template"
                          label="Aus Vorlage erstellen"
                          // Hinweis im Feld statt darunter: Dort steht er da,
                          // wo der Disponent ohnehin hinsieht, und kostet
                          // keine zusätzliche Zeile.
                          placeholder={templateHint}
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            width: '100%',
                            minWidth: { xs: 0, sm: '350px' },
                            // Solange nichts zu wählen ist, soll das Feld auch
                            // aussehen wie ein gesperrtes – MUI graut sonst nur
                            // Text und Rahmen leicht ab. Bewusst helles Grau:
                            // Auf `action.disabledBackground` wäre der Hinweis
                            // als Platzhalter kaum noch zu lesen.
                            ...(!templatesSelectable && {
                              '& .MuiOutlinedInput-root': { bgcolor: 'grey.100' },
                              '& .MuiInputBase-input::placeholder': { opacity: 0.85 },
                              '& .MuiInputBase-input.Mui-disabled::placeholder': { opacity: 0.85 },
                            }),
                          }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <TemplateIcon color="primary" sx={{ mr: 1 }} />
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      ListboxProps={{
                        style: { 
                          maxWidth: 'none',
                          minWidth: 'min(450px, calc(100vw - 32px))'
                        }
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box sx={{ width: '100%', py: 0.5 }}>
                            <Typography 
                              sx={{ 
                                fontSize: '0.875rem', 
                                fontWeight: option.customer_name ? 'bold' : 'normal',
                                lineHeight: 1.3
                              }}
                            >
                              {option.customer_name ? (
                                <span style={{ color: 'primary.main' }}>{option.customer_name}:</span>
                              ) : ''}
                              {' '}{option.pickup_address} → {option.destination_address}
                            </Typography>
                            <Typography 
                              sx={{ 
                                fontSize: '0.75rem', 
                                color: 'text.secondary',
                                lineHeight: 1.2,
                                mt: 0.25
                              }}
                            >
                              {!option.customer_name ? 'Kunde: Nicht angegeben' : ''} 
                              {option.customer_name ? '' : ' | '}
                              Typ: {getVehicleTypeDescription(parseInt(option.vehicle_type))}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Grid>
                )}
                
                {createNewCustomer && (
                  <>
                    <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                      <TextField
                        fullWidth
                        id={fieldId('customer_phone')}
                        name={fieldId('customer_phone')}
                        data-nav="customerPhone"
                        autoComplete={NO_AUTOFILL}
                        label="Telefonnummer (optional)"
                        value={formData.customer_phone}
                        onChange={handleCustomerPhoneChange}
                        error={!!formErrors.customer_phone}
                        helperText={formErrors.customer_phone || "Kann später in der Kundenverwaltung ergänzt werden"}
                        placeholder="+49123456789"
                        sx={{ maxWidth: { xs: 'none', md: '400px' } }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Alert severity="info">
                        Ein neuer Telefonkunde wird erstellt. Die Kundeninformationen können später in der Kundenverwaltung bearbeitet werden.
                      </Alert>
                    </Grid>
                  </>
                )}
              </Grid>
            </FormPaper>
            
            <OrderTypeBox>
              <Typography variant="h6" gutterBottom>
                Bestelltyp
              </Typography>
              
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: formData.order_type === '2' ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        bgcolor: formData.order_type === '2' ? 'primary.light' : 'background.paper',
                        '&:hover': {
                          bgcolor: formData.order_type === '2' ? 'primary.light' : 'action.hover'
                        },
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleOrderTypeChange({ target: { value: '2' } })}
                    >
                      <FormControlLabel 
                        value="2" 
                        control={<Radio 
                          checked={formData.order_type === '2'} 
                          sx={{
                            color: '#000000',
                            '&.Mui-checked': {
                              color: '#000000'
                            }
                          }}
                        />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <ImmediateIcon sx={{ mr: 1 }} />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Sofortbestellung</Typography>
                              <Typography variant="body2" color="text.secondary">Fahrt beginnt sofort nach Fahrerzuweisung</Typography>
                            </Box>
                          </Box>
                        } 
                        sx={{ margin: 0, width: '100%' }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: formData.order_type === '1' ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        bgcolor: formData.order_type === '1' ? 'primary.light' : 'background.paper',
                        '&:hover': {
                          bgcolor: formData.order_type === '1' ? 'primary.light' : 'action.hover'
                        },
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleOrderTypeChange({ target: { value: '1' } })}
                    >
                      <FormControlLabel 
                        value="1" 
                        control={<Radio 
                          checked={formData.order_type === '1'} 
                          sx={{
                            color: '#000000',
                            '&.Mui-checked': {
                              color: '#000000'
                            }
                          }}
                        />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <ScheduleIcon sx={{ mr: 1 }} />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Vorbestellung</Typography>
                              <Typography variant="body2" color="text.secondary">Fahrt für einen späteren Zeitpunkt planen</Typography>
                            </Box>
                          </Box>
                        } 
                        sx={{ margin: 0, width: '100%' }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: formData.order_type === '3' ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        bgcolor: formData.order_type === '3' ? 'primary.light' : 'background.paper',
                        '&:hover': {
                          bgcolor: formData.order_type === '3' ? 'primary.light' : 'action.hover'
                        },
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleOrderTypeChange({ target: { value: '3' } })}
                    >
                      <FormControlLabel 
                        value="3" 
                        control={<Radio 
                          checked={formData.order_type === '3'} 
                          sx={{
                            color: '#000000',
                            '&.Mui-checked': {
                              color: '#000000'
                            }
                          }}
                        />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <RecurringIcon sx={{ mr: 1 }} />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Dauerauftrag</Typography>
                              <Typography variant="body2" color="text.secondary">Wiederkehrende Fahrten (z.B. Kranken-/Dialysefahrten)</Typography>
                            </Box>
                          </Box>
                        } 
                        sx={{ margin: 0, width: '100%' }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </FormControl>
              
              {formData.order_type === '1' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Wann soll die Fahrt stattfinden?
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                        <DateTimePicker
                          label="Datum und Uhrzeit"
                          value={formData.scheduled_time}
                          onChange={handleScheduledTimeChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              variant: "outlined",
                              error: !!formErrors.scheduled_time,
                              'data-nav': 'scheduledTime',
                              helperText: formErrors.scheduled_time || "Wählen Sie Datum und Uhrzeit für die Vorbestellung",
                              InputProps: {
                                style: { backgroundColor: 'white' }
                              }
                            },
                            openPickerButton: { tabIndex: -1 }
                          }}
                          minDate={new Date()}
                          ampm={false}
                          format="dd.MM.yyyy HH:mm"
                          minutesStep={5}
                          views={['year', 'month', 'day', 'hours', 'minutes']}
                          // Kalender mit größeren Flächen für mobile Bedienung
                          componentsProps={{
                            actionBar: {
                              actions: ['clear', 'today', 'accept'],
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    {formData.scheduled_time && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Geplanter Zeitpunkt
                          </Typography>
                          <Typography>
                            {new Date(formData.scheduled_time).toLocaleString('de-DE', {
                              weekday: 'long',
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
              
              {formData.order_type === '3' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Dauerauftrag konfigurieren
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {/* Zeitraum */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                        <DatePicker
                          label="Startdatum"
                          value={formData.recurring_start_date}
                          onChange={handleRecurringStartDateChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              variant: "outlined",
                              error: !!formErrors.recurring_start_date,
                              'data-nav': 'recurringStart',
                              helperText: formErrors.recurring_start_date || "Ab wann soll der Dauerauftrag beginnen?",
                              InputProps: {
                                style: { backgroundColor: 'white' }
                              }
                            },
                            openPickerButton: { tabIndex: -1 }
                          }}
                          minDate={new Date()}
                          format="dd.MM.yyyy"
                        />
                      </LocalizationProvider>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                        <DatePicker
                          label="Enddatum"
                          value={formData.recurring_end_date}
                          onChange={handleRecurringEndDateChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              variant: "outlined",
                              error: !!formErrors.recurring_end_date,
                              'data-nav': 'recurringEnd',
                              helperText: formErrors.recurring_end_date || "Bis wann soll der Dauerauftrag laufen?",
                              InputProps: {
                                style: { backgroundColor: 'white' }
                              }
                            },
                            openPickerButton: { tabIndex: -1 }
                          }}
                          minDate={formData.recurring_start_date || new Date()}
                          format="dd.MM.yyyy"
                        />
                      </LocalizationProvider>
                    </Grid>
                    
                    {/* Uhrzeit */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                        <TimePicker
                          label="Abholzeit"
                          value={formData.recurring_time}
                          onChange={handleRecurringTimeChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              variant: "outlined",
                              error: !!formErrors.recurring_time,
                              'data-nav': 'recurringTime',
                              helperText: formErrors.recurring_time || "Um welche Uhrzeit soll abgeholt werden?",
                              InputProps: {
                                style: { backgroundColor: 'white' }
                              }
                            },
                            openPickerButton: { tabIndex: -1 }
                          }}
                          ampm={false}
                          format="HH:mm"
                          minutesStep={5}
                        />
                      </LocalizationProvider>
                    </Grid>
                    
                    {/* Wochentage */}
                    <Grid size={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Wochentage auswählen *
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1,
                        maxWidth: { xs: '100%', md: '50%' }
                      }}>
                        {[
                          { index: 1, label: 'Mo' },
                          { index: 2, label: 'Di' },
                          { index: 3, label: 'Mi' },
                          { index: 4, label: 'Do' },
                          { index: 5, label: 'Fr' },
                          { index: 6, label: 'Sa' },
                          { index: 0, label: 'So' }
                        ].map((day) => (
                          <FormControlLabel
                            key={day.index}
                            control={
                              <Checkbox
                                checked={formData.recurring_weekdays.includes(day.index)}
                                onChange={() => handleWeekdayChange(day.index)}
                                color="primary"
                                sx={{
                                  color: '#000000', // Schwarze Checkbox
                                  '&.Mui-checked': {
                                    color: '#000000' // Schwarze Markierung wenn ausgewählt
                                  },
                                  padding: '4px' // Reduziertes Padding um Checkbox
                                }}
                              />
                            }
                            label={
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 'bold', 
                                  textAlign: 'center',
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' } // Kleinere Schrift auf Mobile
                                }}
                              >
                                {day.label}
                              </Typography>
                            }
                            sx={{ 
                              margin: 0,
                              border: formData.recurring_weekdays.includes(day.index) 
                                ? '2px solid' 
                                : '1px solid',
                              borderColor: formData.recurring_weekdays.includes(day.index) 
                                ? 'primary.main' 
                                : 'divider',
                              borderRadius: 1,
                              p: { xs: 1, sm: 1.5 }, // Weniger Padding auf Mobile
                              flex: '1 1 calc((100% - 6px) / 7)', // 7 Container mit Gaps
                              minWidth: { xs: 40, sm: 60 }, // Noch kleinere Container auf Mobile
                              maxWidth: { xs: 50, sm: 100 },
                              minHeight: 48,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              bgcolor: formData.recurring_weekdays.includes(day.index) 
                                ? 'primary.light' 
                                : 'background.paper',
                              '&:hover': {
                                bgcolor: formData.recurring_weekdays.includes(day.index) 
                                  ? 'primary.light' 
                                  : 'action.hover'
                              },
                              '& .MuiFormControlLabel-label': {
                                width: '100%',
                                textAlign: 'center',
                                marginLeft: '4px' // Reduzierter Abstand zwischen Checkbox und Label
                              }
                            }}
                          />
                        ))}
                      </Box>
                      {formErrors.recurring_weekdays && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {formErrors.recurring_weekdays}
                        </FormHelperText>
                      )}
                    </Grid>
                    
                    {/* Vorschau der geplanten Termine */}
                    {formData.recurring_start_date && formData.recurring_end_date && 
                     formData.recurring_time && formData.recurring_weekdays.length > 0 && (
                      <Grid size={12}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Vorschau der geplanten Termine
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Die erste Fahrt wird erstellt für:{' '}
                            {(() => {
                              const weekdayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
                              return formData.recurring_weekdays.map(day => weekdayNames[day]).join(', ');
                            })()}
                            {' '}um {formData.recurring_time ? new Date(formData.recurring_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Zeitraum:</strong> {formData.recurring_start_date ? new Date(formData.recurring_start_date).toLocaleDateString('de-DE') : ''} bis{' '}
                            {formData.recurring_end_date ? new Date(formData.recurring_end_date).toLocaleDateString('de-DE') : ''}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </OrderTypeBox>
            
            <FormPaper elevation={2} sx={{ position: 'relative', overflow: 'visible' }}>
              <Typography variant="h6" gutterBottom>
                Fahrtinformationen
              </Typography>
              
              <Grid container spacing={3}>
                <Grid size={12}>
                  <PlaceAutocomplete
                    navName="pickupAddress"
                    required
                    fullWidth
                    label="Abholadresse"
                    value={formData.pickup_address}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      pickup_address: e.target.value,
                      // Nach einer Auswahl meldet das Google-Element den Text
                      // selbst noch einmal als Eingabe — nur ein abweichender
                      // Text ist wirklich von Hand getippt.
                      pickup_location: e.target.value === selectedAddressRef.current.pickup
                        ? prev.pickup_location
                        : { latitude: null, longitude: null }
                    }))}
                    onPlaceSelect={(address, location) => {
                      selectedAddressRef.current.pickup = address;
                      setFormData(prev => ({
                        ...prev,
                        pickup_address: address,
                        pickup_location: location
                      }));
                    }}
                    error={!!formErrors.pickup_address}
                    helperText={formErrors.pickup_address}
                    placeholder="Straße, Hausnummer, PLZ, Ort"
                  />
                </Grid>

                <Grid size={12} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, paddingTop: '0 !important' }}>
                  <Tooltip title="Start- und Zieladresse tauschen">
                    <span>
                      <IconButton
                        onClick={handleSwapAddresses}
                        disabled={!formData.pickup_address && !formData.destination_address}
                        size="small"
                        color="primary"
                        sx={{
                          width: 44,
                          height: 44,
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <SwapIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>

                <Grid size={12} sx={{ paddingTop: '0 !important', mt: -4 }}>
                  <PlaceAutocomplete
                    navName="destinationAddress"
                    fullWidth
                    label="Zieladresse"
                    value={formData.destination_address}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      destination_address: e.target.value,
                      destination_location: e.target.value === selectedAddressRef.current.destination
                        ? prev.destination_location
                        : { latitude: null, longitude: null }
                    }))}
                    onPlaceSelect={(address, location) => {
                      selectedAddressRef.current.destination = address;
                      setFormData(prev => ({
                        ...prev,
                        destination_address: address,
                        destination_location: location
                      }));
                    }}
                    error={!!formErrors.destination_address}
                    helperText={formErrors.destination_address}
                    placeholder="Straße, Hausnummer, PLZ, Ort"
                  />
                </Grid>

                {/*
                  Zwischenstopps (zwischenstopps_konzept.md §3.2). Reihenfolge:
                  Abholung → Zwischenstopps in eingegebener Folge → Ziel; es
                  wird nichts automatisch optimiert. Verordnungsfahrten sind in
                  Phase 1 ausgenommen (§10.6) — der Kostenträger bezahlt eine
                  Strecke.
                */}
                {!formData.is_krankenfahrt && formData.order_type !== '3' && (formData.stops || []).map((stop, index) => (
                  <Grid size={12} key={`stop-${index}`} sx={{ paddingTop: '0 !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <PlaceAutocomplete
                        fullWidth
                        label={`Zwischenstopp ${index + 1}`}
                        value={stop.address}
                        onChange={(e) => setFormData(prev => {
                          const stops = [...prev.stops];
                          stops[index] = {
                            address: e.target.value,
                            // Getippter Text verwirft die Koordinaten des
                            // Vorschlags; resolveFormCoordinates geocodiert
                            // vor dem Speichern nach.
                            location: { latitude: null, longitude: null },
                          };
                          return { ...prev, stops };
                        })}
                        onPlaceSelect={(address, location) => setFormData(prev => {
                          const stops = [...prev.stops];
                          stops[index] = { address, location };
                          return { ...prev, stops };
                        })}
                        error={!!formErrors[`stop_${index}`]}
                        helperText={formErrors[`stop_${index}`]}
                        placeholder="Straße, Hausnummer, PLZ, Ort"
                      />
                      {(formData.stops || []).length > 1 && (
                        <>
                          <Tooltip title="Nach oben">
                            <span>
                              <IconButton
                                size="small"
                                disabled={index === 0}
                                onClick={() => setFormData(prev => {
                                  const stops = [...prev.stops];
                                  [stops[index - 1], stops[index]] = [stops[index], stops[index - 1]];
                                  return { ...prev, stops };
                                })}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Nach unten">
                            <span>
                              <IconButton
                                size="small"
                                disabled={index === (formData.stops || []).length - 1}
                                onClick={() => setFormData(prev => {
                                  const stops = [...prev.stops];
                                  [stops[index + 1], stops[index]] = [stops[index], stops[index + 1]];
                                  return { ...prev, stops };
                                })}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Zwischenstopp entfernen">
                        <IconButton
                          size="small"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            stops: prev.stops.filter((_, i) => i !== index),
                          }))}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                ))}

                {!formData.is_krankenfahrt && formData.order_type !== '3' && (
                  <Grid size={12} sx={{ paddingTop: '0 !important' }}>
                    <Button
                      size="small"
                      startIcon={<AddLocationAltIcon />}
                      disabled={(formData.stops || []).length >= MAX_INTERMEDIATE_STOPS}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        stops: [...(prev.stops || []), { address: '', location: { latitude: null, longitude: null } }],
                        // Ein Festpreis gilt für die Relation Start→Ziel und
                        // passt zur gefahrenen Strecke nicht mehr (§5.3).
                        is_festpreis: false,
                        festpreis_eur: '',
                      }))}
                    >
                      {(formData.stops || []).length >= MAX_INTERMEDIATE_STOPS
                        ? `Maximal ${MAX_INTERMEDIATE_STOPS} Zwischenstopps`
                        : '+ Zwischenstopp'}
                    </Button>
                    {(formData.stops || []).length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Bei Fahrten mit Zwischenstopp gilt der Kilometerpreis, kein Festpreis.
                      </Typography>
                    )}
                  </Grid>
                )}
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="vehicle-type-label">Fahrzeugtyp</InputLabel>
                    <Select
                      labelId="vehicle-type-label"
                      id="vehicle_type"
                      name="vehicle_type"
                      data-nav="vehicleType"
                      data-nav-type="select"
                      value={formData.vehicle_type}
                      onChange={handleChange}
                      label="Fahrzeugtyp"
                    >
                      {VEHICLE_TYPE_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={String(opt.value)}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/*
                  Das Feld bleibt immer stehen, auch wenn es nichts zu wählen
                  gibt: Sonst erschiene es mitten in der Erfassung und schöbe
                  die Felder darunter weg (docs/TASTATURGEFUEHRTE_FAHRTERFASSUNG.md
                  3.6). Der Hilfetext sagt dann, woran es liegt.
                */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth disabled={specialCapabilityOptions.length === 0}>
                    <InputLabel id="special-capabilities-label">Spezialfähigkeiten</InputLabel>
                    <Select
                      labelId="special-capabilities-label"
                      id="special_capabilities"
                      data-nav="specialCapabilities"
                      data-nav-type="select"
                      multiple
                      value={formData.special_capabilities}
                      onChange={(e) => setFormData({ ...formData, special_capabilities: e.target.value })}
                      label="Spezialfähigkeiten"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map(s => (
                            <Chip
                              key={s}
                              label={SPECIAL_CAPABILITIES[s] || s}
                              size="small"
                              color={unavailableSelectedCapabilities.has(s) ? 'warning' : 'default'}
                              variant={unavailableSelectedCapabilities.has(s) ? 'outlined' : 'filled'}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {specialCapabilityOptions.map(opt => (
                        <MenuItem key={opt.key} value={opt.key} dense disabled={opt.disabled}>
                          <Checkbox
                            checked={formData.special_capabilities.includes(opt.key)}
                            size="small"
                            disabled={opt.disabled}
                            sx={{ py: 0, mr: 1 }}
                          />
                          <ListItemText primary={opt.label} secondary={opt.hint || null} />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {specialCapabilityOptions.length === 0
                        ? 'Für die angelegten Fahrzeuge sind keine Spezialfähigkeiten hinterlegt'
                        : formData.order_type === '2'
                          ? 'Mehrfachauswahl möglich — ausgegraute Fähigkeiten sind gerade nicht im Dienst'
                          : 'Mehrfachauswahl möglich'}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap' }}>
                    {/* Kranken- und (Firmen-)Rechnungsfahrt schließen sich aus
                        (getrennte Abrechnungswege, rechnungskunden_konzept §2):
                        das Aktivieren der einen deaktiviert die andere. */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          data-nav="krankenfahrt"
                          checked={formData.is_krankenfahrt}
                          onChange={(e) => {
                            if (!e.target.checked) setCreateNewMedicalOrder(false);
                            if (e.target.checked) setNewInvoiceCustomerName('');
                            setFormData({
                              ...formData,
                              is_krankenfahrt: e.target.checked,
                              medical_transport_order_id: e.target.checked ? formData.medical_transport_order_id : '',
                              ...(e.target.checked ? { is_rechnungsfahrt: false, invoice_customer_id: '' } : {})
                            });
                          }}
                        />
                      }
                      label="Krankenfahrt"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          data-nav="rechnungsfahrt"
                          checked={formData.is_rechnungsfahrt}
                          onChange={(e) => {
                            if (e.target.checked) setCreateNewMedicalOrder(false);
                            if (!e.target.checked) setNewInvoiceCustomerName('');
                            setFormData({
                              ...formData,
                              is_rechnungsfahrt: e.target.checked,
                              invoice_customer_id: e.target.checked ? formData.invoice_customer_id : '',
                              ...(e.target.checked ? { is_krankenfahrt: false, medical_transport_order_id: '' } : {})
                            });
                          }}
                        />
                      }
                      label="Rechnungsfahrt"
                    />
                    {formData.is_rechnungsfahrt && (
                      <Autocomplete
                        options={invoiceCustomers}
                        freeSolo
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        getOptionLabel={(o) => {
                          if (typeof o === 'string') return o;
                          if (o.isCreateNew) return `Neuer Rechnungskunde: ${o.inputValue} (wird beim Speichern angelegt)`;
                          return `${o.customerNumber ? o.customerNumber + ' · ' : ''}${o.displayName || o.companyName || ''}`;
                        }}
                        isOptionEqualToValue={(o, v) => o.isCreateNew
                          ? v?.isCreateNew === true
                          : o.id === v?.id}
                        filterOptions={(options, { inputValue }) => {
                          const term = (inputValue || '').toLowerCase();
                          const filtered = term
                            ? options.filter((o) =>
                                `${o.customerNumber || ''} ${o.displayName || ''} ${o.companyName || ''}`.toLowerCase().includes(term))
                            : [...options];
                          // Schnellanlage (§8.4): getippter Name wird Firmenname.
                          const trimmed = (inputValue || '').trim();
                          const exists = options.some((o) =>
                            (o.displayName || '').toLowerCase() === trimmed.toLowerCase() ||
                            (o.companyName || '').toLowerCase() === trimmed.toLowerCase());
                          if (trimmed && !exists) {
                            filtered.push({ inputValue: trimmed, isCreateNew: true });
                          }
                          return filtered;
                        }}
                        value={newInvoiceCustomerName
                          ? { inputValue: newInvoiceCustomerName, isCreateNew: true }
                          : invoiceCustomers.find((c) => c.id === formData.invoice_customer_id) || null}
                        onChange={(e, val) => {
                          if (typeof val === 'string' || val?.isCreateNew) {
                            const name = (typeof val === 'string' ? val : val.inputValue).trim();
                            setNewInvoiceCustomerName(name);
                            setFormData(prev => ({ ...prev, invoice_customer_id: '' }));
                          } else {
                            setNewInvoiceCustomerName('');
                            setFormData(prev => ({ ...prev, invoice_customer_id: val ? val.id : '' }));
                          }
                          setFormErrors(prev => ({ ...prev, invoice_customer_id: '' }));
                        }}
                        renderOption={(props, option) => {
                          if (option.isCreateNew) {
                            return (
                              <Box component="li" {...props} sx={{ display: 'block !important' }}>
                                <Typography variant="body2" color="primary" fontWeight="medium">
                                  ＋ „{option.inputValue}" als neuen Rechnungskunden anlegen
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Wird unvollständig angelegt — Anschrift und Rechnungs-E-Mail ergänzt die Zentrale;
                                  bis dahin ist die Rechnungserstellung gesperrt.
                                </Typography>
                              </Box>
                            );
                          }
                          return (
                            <Box component="li" {...props}>
                              <Typography variant="body2">
                                {option.customerNumber ? `${option.customerNumber} · ` : ''}{option.displayName || option.companyName || ''}
                                {option.billingProfileComplete === false ? ' (unvollständig)' : ''}
                              </Typography>
                            </Box>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputProps={{ ...params.inputProps, autoComplete: NO_AUTOFILL }}
                            label="Rechnungskunde"
                            required
                            error={!!formErrors.invoice_customer_id}
                            helperText={formErrors.invoice_customer_id || 'Firma, die diese Fahrt bezahlt — tippen zum Suchen oder neu anlegen'}
                          />
                        )}
                        sx={{ minWidth: { xs: 0, sm: 320 }, width: { xs: '100%', sm: 'auto' } }}
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          data-nav="festpreis"
                          checked={formData.is_festpreis}
                          onChange={(e) => setFormData({ ...formData, is_festpreis: e.target.checked, festpreis_eur: e.target.checked ? formData.festpreis_eur : '' })}
                        />
                      }
                      label="Festpreis"
                    />
                    {formData.is_festpreis && (
                      <TextField
                        type="number"
                        data-nav="festpreisBetrag"
                        autoComplete={NO_AUTOFILL}
                        label="Festpreis-Betrag"
                        value={formData.festpreis_eur}
                        onChange={(e) => setFormData({ ...formData, festpreis_eur: e.target.value })}
                        placeholder="z.B. 25.00"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                          inputProps: { min: 0, step: 0.5 }
                        }}
                        helperText="Betrag in Euro eingeben (z.B. 25.00)"
                        sx={{ minWidth: { xs: 0, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}
                      />
                    )}
                  </Box>
                </Grid>

                {/* Preisschätzung (Tarif + ggf. Anfahrtskosten). Immer sichtbar,
                    sobald eine Preis-Basis vorliegt (Route oder Festpreis). */}
                {(() => {
                  const hasRoute = coordsOf(formData.pickup_location) && coordsOf(formData.destination_location);
                  const hasFestpreis = formData.is_festpreis && formData.festpreis_eur;
                  if (!hasRoute && !hasFestpreis) return null;
                  const fmt = (n) => Number(n).toFixed(2).replace('.', ',');
                  const est = fareEstimate;
                  return (
                    <Grid size={12}>
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: est ? 1 : 0 }}>
                          <EuroIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight="medium">
                            {est?.isFestpreis ? 'Festpreis' : 'Geschätzter Fahrpreis'}
                          </Typography>
                          {isCalculatingFare && <CircularProgress size={16} sx={{ ml: 1 }} />}
                        </Box>

                        {est && est.totalFareEur !== null && (
                          <>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {est.isFestpreis ? '' : 'ca. '}{fmt(est.totalFareEur)} €
                            </Typography>
                            {est.anfahrt && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <AnfahrtIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                <Typography variant="body2" color="warning.main">
                                  davon Anfahrt {fmt(est.anfahrt.eur)} € ({est.anfahrt.km.toFixed(1).replace('.', ',')} km außerhalb Betriebsgebiet)
                                </Typography>
                              </Box>
                            )}
                            {est.zuschlag && est.zuschlag.eur > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <WheelchairIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                <Typography variant="body2" color="warning.main">
                                  davon {spezialZuschlagLabel(est.zuschlag.grund)} {fmt(est.zuschlag.eur)} € (statt Großraumzuschlag)
                                </Typography>
                              </Box>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {est.distanceKm != null && `Entfernung ca. ${fmt(est.distanceKm)} km`}
                              {est.nightTariff && ' · Nachttarif'}
                              {!est.isFestpreis && ' · Richtwert nach Taxameter, kann je nach Route/Verkehr abweichen'}
                            </Typography>
                          </>
                        )}

                        {est && est.totalFareEur === null && !isCalculatingFare && (
                          <Typography variant="body2" color="text.secondary">
                            Für die Schätzung bitte Abhol- und Zieladresse mit Koordinaten wählen.
                          </Typography>
                        )}

                        {!est && !isCalculatingFare && (
                          <Typography variant="body2" color="text.secondary">
                            Preisschätzung wird vorbereitet …
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })()}

                {formData.is_krankenfahrt && (
                  <Grid size={12}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <MedicalIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="medium">
                          Verordnung zuordnen
                        </Typography>
                      </Box>
                      {/* Hinweis gemäß Verordnungspflicht-Konzept §3: Privat-
                          zahler sind fachlich keine Krankenfahrt. */}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Privatversicherte ohne Kassen-Verordnung? Keine Krankenfahrt ankreuzen — normale Fahrt anlegen
                        (Kunde erhält Quittung) oder, wenn eine Institution zahlt, Rechnungsfahrt mit Rechnungskunde.
                      </Typography>

                      <Autocomplete
                        fullWidth
                        options={medicalOrdersForCustomer}
                        loading={medicalTransportOrdersLoading}
                        value={createNewMedicalOrder ? MEDICAL_ORDER_CREATE_OPTION : selectedMedicalTransportOrder}
                        onChange={handleMedicalTransportOrderChange}
                        getOptionLabel={(option) => {
                          if (!option) return '';
                          if (option.isCreatePlaceholder) {
                            const patientName = (formData.customer_name || '').trim();
                            return patientName
                              ? `Neue Verordnung für „${patientName}" (wird beim Speichern angelegt)`
                              : 'Neue Verordnung (Patient wird aus dem Fahrt-Kunden übernommen)';
                          }
                          const remaining = Number(option.maxRides || 0) > 0
                            ? `, Rest: ${Math.max(Number(option.maxRides || 0) - Number(option.usedRides || 0) - Number(option.plannedRides || 0), 0)}`
                            : '';
                          return `${option.patientName || 'Ohne Patient'} - ${option.healthInsuranceName || 'Ohne Krankenkasse'} (${formatDate(option.validFrom)} bis ${formatDate(option.validUntil)}${remaining})`;
                        }}
                        isOptionEqualToValue={(option, value) => option.isCreatePlaceholder
                          ? value?.isCreatePlaceholder === true
                          : option.id === value?.id}
                        filterOptions={(options, { inputValue }) => {
                          const term = (inputValue || '').toLowerCase();
                          const filtered = term
                            ? options.filter((option) =>
                                `${option.patientName || ''} ${option.healthInsuranceName || ''}`.toLowerCase().includes(term))
                            : [...options];
                          // Schnellanlage-Option immer als letzten Eintrag anbieten (§8.1).
                          filtered.push(MEDICAL_ORDER_CREATE_OPTION);
                          return filtered;
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputProps={{ ...params.inputProps, autoComplete: NO_AUTOFILL }}
                            label="Verordnungsfall"
                            helperText={
                              formErrors.medical_transport_order_id
                              || (medicalOrdersFilteredByCustomer
                                ? (medicalOrdersForCustomer.length === 0
                                  ? `Keine Verordnung für ${formData.customer_name || 'diesen Kunden'} hinterlegt — „Neue Verordnung anlegen" nutzen.`
                                  : `Gefiltert nach ${formData.customer_name || 'dem gewählten Kunden'}. Kunden oben leeren, um alle Verordnungen zu sehen.`)
                                : 'Pflicht bei Krankenfahrt: bestehende Verordnung wählen oder „Neue Verordnung anlegen" nutzen.')
                            }
                            error={!!formErrors.medical_transport_order_id}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {medicalTransportOrdersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                        renderOption={(props, option) => {
                          if (option.isCreatePlaceholder) {
                            const patientName = (formData.customer_name || '').trim();
                            return (
                              <Box component="li" {...props} sx={{ display: 'block !important' }}>
                                <Typography variant="body2" color="primary" fontWeight="medium">
                                  ＋ Neue Verordnung anlegen{patientName ? ` (Patient: ${patientName})` : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {patientName
                                    ? 'Wird beim Speichern als unvollständige Verordnung angelegt — Details pflegt die Zentrale nach.'
                                    : 'Patient wird automatisch aus dem Fahrt-Kunden übernommen — bitte zuerst Kunden wählen.'}
                                </Typography>
                              </Box>
                            );
                          }
                          const approval = getOption(APPROVAL_STATUS_OPTIONS, option.approvalStatus);
                          const maxRides = Number(option.maxRides) || 0;
                          const remaining = maxRides > 0
                            ? Math.max(maxRides - Number(option.usedRides || 0) - Number(option.plannedRides || 0), 0)
                            : null;

                          return (
                            <Box component="li" {...props} sx={{ display: 'block !important' }}>
                              <Typography variant="body2" fontWeight="medium">
                                {option.patientName || 'Ohne Patient'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {option.healthInsuranceName || 'Ohne Krankenkasse'} · {formatDate(option.validFrom)} bis {formatDate(option.validUntil)}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip size="small" label={approval.label} color={approval.color} />
                                <Chip
                                  size="small"
                                  label={remaining === null ? 'Kein Kontingent' : `${remaining} Restfahrten`}
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                          );
                        }}
                      />

                      {selectedMedicalTransportOrder && (
                        <Alert severity={medicalTransportOrderCheck.severity} sx={{ mt: 2 }}>
                          {medicalTransportOrderCheck.messages.map((message) => (
                            <Typography key={message} variant="body2">
                              {message}
                            </Typography>
                          ))}
                        </Alert>
                      )}
                      {/* Harte Hinweise (Kontingent/Verordnungszeitraum) sind
                          übersteuerbar: bewusste Bestätigung statt Blockade. */}
                      {selectedMedicalTransportOrder && medicalTransportOrderCheck.severity === 'error' && (
                        <FormControlLabel
                          sx={{ mt: 1, alignItems: 'flex-start' }}
                          control={
                            <Checkbox
                              color="warning"
                              checked={medicalOverrideAck}
                              onChange={(e) => setMedicalOverrideAck(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Hinweis verstanden — Fahrt bewusst trotz des obigen Verordnungs-Hinweises anlegen.
                            </Typography>
                          }
                        />
                      )}
                      {createNewMedicalOrder && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Beim Speichern wird eine unvollständige Platzhalter-Verordnung
                          {(formData.customer_name || '').trim() ? ` für „${formData.customer_name.trim()}"` : ''} angelegt
                          und mit der Fahrt verknüpft. Krankenkasse, Zeitraum und Kontingent bitte anschließend in der
                          Verordnungsverwaltung ergänzen.
                        </Alert>
                      )}
                    </Box>
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="notes"
                    name="notes"
                    data-nav="notes"
                    data-nav-type="multiline"
                    autoComplete={NO_AUTOFILL}
                    label="Notizen für den Fahrer"
                    placeholder="z.B. Kunde wartet am Hintereingang, Rollstuhlfahrer, ..."
                    multiline
                    minRows={2}
                    maxRows={4}
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </Grid>

                {!isEditMode && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={saveAsTemplate}
                          onChange={(e) => setSaveAsTemplate(e.target.checked)}
                          icon={<BookmarkIcon />}
                          checkedIcon={<BookmarkIcon />}
                        />
                      }
                      label={
                        <Tooltip title="Die Fahrt wird als Vorlage gespeichert, die später wiederverwendet werden kann">
                          <Typography variant="body2">Als Vorlage speichern</Typography>
                        </Tooltip>
                      }
                    />
                  </Grid>
                )}

              </Grid>
            </FormPaper>

            {/* Direktzuweisung — nur beim Erstellen, nicht beim Bearbeiten */}
            {isEditMode ? (
              <FormPaper elevation={2} sx={{ bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  Fahrerzuweisung
                </Typography>
                <Alert severity="info">
                  Die Fahrerzuweisung kann nicht im Bearbeitungsmodus geändert werden. Bitte nutzen Sie dafür die Funktion „Fahrer zuweisen" in der Fahrtdetailansicht.
                </Alert>
                {currentRide?.driverName && (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Aktuell zugewiesen: <strong>{currentRide.driverName}</strong>
                  </Typography>
                )}
              </FormPaper>
            ) : formData.order_type === '3' ? (
              <FormPaper elevation={2} sx={{ bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  Fahrerzuweisung
                </Typography>
                <Alert severity="info">
                  Ein Dauerauftrag ist eine Vorlage. Fahrer werden pro generierter Einzelfahrt im Zuweisungsmodus vergeben, nicht auf der Vorlage.
                </Alert>
              </FormPaper>
            ) : (
              <FormPaper elevation={2}>
                <Typography variant="h6" gutterBottom>
                  Fahrt direkt zuweisen
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Optional: Wählen Sie einen Fahrer, um die Fahrt direkt zuzuweisen.
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      id={fieldId('driver_id')}
                      options={formData.order_type === '1' ? allDrivers : availableDrivers}
                      getOptionLabel={getDriverOptionLabel}
                      value={(formData.order_type === '1' ? allDrivers : availableDrivers).find(d => d.id === formData.driver_id) || null}
                      onChange={handleDriverChange}
                      getOptionDisabled={isDriverOptionDisabled}
                      renderInput={(params) =>
                        <TextField
                          {...params}
                          inputProps={{ ...params.inputProps, autoComplete: NO_AUTOFILL }}
                          data-nav="driver"
                          label="Fahrer auswählen"
                          fullWidth
                          error={!!formErrors.driver_id}
                          helperText={formErrors.driver_id || (
                            formData.order_type === '1'
                              ? "Für Vorbestellungen können alle Fahrer zugewiesen werden."
                              : "Für Sofortbestellungen können nur Online-Fahrer mit zugewiesenem, passendem Fahrzeug zugewiesen werden."
                          )}
                        />
                      }
                      renderOption={(props, option) => renderDriverOption(props, option, {
                        requireOnlineWithVehicle: formData.order_type !== '1',
                        mismatchReason: getDriverMismatchReason(option),
                        blocking: isDriverOptionDisabled(option)
                      })}
                    />
                    {selectedDriverMismatch && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Dieser Fahrer erfüllt die Anforderung der Fahrt nicht: {selectedDriverMismatch}.
                      </Alert>
                    )}
                  </Grid>
                </Grid>

                {formData.driver_id && (
                  <>
                    {formData.order_type === '1' && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Bei zugewiesenen Vorbestellungen prüft NextCab ab dem berechneten Freigabezeitpunkt,
                        ob der Fahrer online ist. Bei externen Abholungen kann diese Prüfung entsprechend der
                        Google-Maps-Fahrzeit früher erfolgen. Ist der Fahrer offline, wird die Fahrt wieder zur
                        Disposition freigegeben.
                      </Alert>
                    )}
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {formData.order_type === '1'
                        ? "Fahrzeugdaten werden erst bei Fahrtantritt durch die Mobile-App gesetzt."
                        : "Bei Zuweisung eines Fahrers für eine Sofortbestellung wird der Status auf 'Akzeptiert' gesetzt."}
                    </Alert>
                  </>
                )}
              </FormPaper>
            )}
          </>
        )}
        
        {/*
          Platzhalter für die mobil fixierte Buttonleiste: Sie liegt ausserhalb
          des Flusses und wuerde sonst das letzte Formularfeld verdecken.
        */}
        <Box aria-hidden sx={{ height: `${actionBarHeight}px`, flexShrink: 0 }} />

        {/* Buttons */}
        <Box
          ref={actionBarRef}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            position: { xs: 'fixed', sm: 'static' },
            left: { xs: 0, sm: 'auto' },
            right: { xs: 0, sm: 'auto' },
            bottom: { xs: 0, sm: 'auto' },
            zIndex: { xs: 20, sm: 'auto' },
            p: { xs: 1.5, sm: 0 },
            bgcolor: { xs: 'background.paper', sm: 'transparent' },
            borderTop: { xs: '1px solid', sm: 'none' },
            borderColor: { xs: 'divider', sm: 'transparent' },
            boxShadow: { xs: 3, sm: 'none' },
          }}
        >
          <Button
            data-nav="submit"
            data-nav-type="action"
            type="button"
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{ order: 3 }}
            startIcon={isCalculatingRoute ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={isLoading || isCalculatingRoute}
            fullWidth={isMobile}
          >
            {isCalculatingRoute ? 'Fahrtdauer wird berechnet...' :
             isAssignMode ? 'Fahrer zuweisen' : 
             isEditMode ? 'Änderungen speichern' : 
             'Fahrt erstellen'}
          </Button>
                  {!isEditMode && !isAssignMode && (
            <Tooltip title={canSaveTemplate
              ? 'Aktuelle Eingaben als Vorlage für diesen Kunden speichern (ohne Fahrt anzulegen)'
              : 'Nur mit ausgewähltem Kunden sowie Abhol- und Zieladresse möglich'}>
              <span style={{ width: isMobile ? '100%' : 'auto', order: 2 }}>
                <Button
                  data-nav="saveTemplate"
                  data-nav-type="action"
                  variant="outlined"
                  color="primary"
                  onClick={handleSaveTemplateOnly}
                  startIcon={<BookmarkIcon />}
                  disabled={!canSaveTemplate}
                  fullWidth={isMobile}
                >
                  Nur als Vorlage anlegen
                </Button>
              </span>
            </Tooltip>
          )}
          {/* Abbrechen gibt es nicht mehr als Button: Der Dialog wird oben
              rechts über das X geschlossen (oder per Escape). */}
</Box>
        <DispatchInputHelpDialog open={helpOpen} onClose={closeHelp} />
      </form>
      </Box>
    </APIProvider>
  );
};

export default RideForm;
